import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { Login } from "@/auth/Login";
import { AuthCallback } from "@/auth/AuthCallback";
import { Loader2 } from "lucide-react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import { Bell, ShieldOff, ShieldCheck, Search, Plus, Trash2, Brain, Settings as SettingsIcon, X } from "lucide-react";
import { api } from "@/api";
import { ApertureEye } from "@/components/ApertureEye";
import { AIChatBar } from "@/components/AIChatBar";
import { NoteCard } from "@/components/NoteCard";
import { MediaLightbox } from "@/components/MediaLightbox";
import { BackupPanel } from "@/components/BackupPanel";
import { ChecklistCard } from "@/components/ChecklistCard";
import { GlossaryCard } from "@/components/GlossaryCard";
import { TaskEditor } from "@/components/TaskEditor";
import { Screensaver } from "@/components/Screensaver";
import { OnboardingDialog } from "@/components/OnboardingDialog";
import { MainNav, SubNav, SUB_TABS } from "@/components/MainNav";
import { NeuralLink } from "@/components/NeuralLink";
import { CompactTaskCard } from "@/components/CompactTaskCard";
import { WeeklyView } from "@/components/WeeklyView";
import { MonthlyView } from "@/components/MonthlyView";
import { RecurringView } from "@/components/RecurringView";
import { MissionChat } from "@/components/MissionChat";
import { QuickCaptureButton } from "@/components/QuickCaptureButton";
import { ManualTaskButton } from "@/components/ManualTaskButton";
import { SettingsDrawer } from "@/components/SettingsDrawer";
import { NagOverlay } from "@/components/NagOverlay";
import { InventoryCard, InventoryAddForm } from "@/components/InventoryCard";
import { BookmarkCard, BookmarkAddForm } from "@/components/BookmarkCard";
import { isTrainingSkipped } from "@/lib/trainingSkip";
import { NotificationsPanel } from "@/components/NotificationsPanel";
import { unlockAudio, onVoiceBlocked, retryBlockedSpeech } from "@/lib/tts";
import { formatDate, todayStr, daysDiff, highlight, linkifyHighlight, linkify, taskOccursOnIso, getOccurrencesOnIso, undoFromForIso, isRecurringMissed, getMissedOccurrences, isOccurrenceSnoozed, isTaskSnoozed, searchScore, effectiveOverrides, normalizeTasksById, overdueEditTarget, occurrenceDueMs, isFutureObligation, effectiveDisplayIso, isStaleRevision, applyUpsert, msUntilNextLocalMidnight } from "@/utils";
import { MissedOccurrencesDialog } from "@/components/MissedOccurrencesDialog";
import { OverdueDosePicker } from "@/components/OverdueDosePicker";
import { DataHygienePanel } from "@/components/DataHygienePanel";
import { DiagnosticsPanel } from "@/components/DiagnosticsPanel";
import { logDiag, rememberTraced, tracedIds, stateSummary } from "@/lib/diagnostics";

// Names we shouldn't treat as a real user identity — e.g. leftover placeholders from
// automated tests or generic strings. Users with any of these will be shown onboarding.
const PLACEHOLDER_NAMES = new Set([
    "test user", "test_user", "testuser", "test",
    "trainer", "user", "guest", "the user",
    "your name", "name", "admin", "example",
]);
const isRealName = (n) => {
    // Normalise: lowercase, strip trailing punctuation, collapse underscores/hyphens to space
    const s = (n || "")
        .trim()
        .replace(/[.!?,;:]+$/g, "")
        .replace(/[_\-]+/g, " ")
        .replace(/\s+/g, " ")
        .toLowerCase();
    if (!s || s.length < 2) return false;
    return !PLACEHOLDER_NAMES.has(s);
};

const EmptyState = ({ label }) => (
    <div className="text-center py-12 text-zinc-600 fade-up">
        <ApertureEye size={52} className="mx-auto mb-3 opacity-40" />
        <p className="font-brand uppercase tracking-widest text-[10px]">nothing here</p>
        <p className="text-[11px] mt-1 font-mono">{label}</p>
    </div>
);

function App() {
    // Auth gate — runs before ANY task/notes state is set up so unauthenticated visitors see a
    // clean login screen instead of empty dashboards flashing on top of a 401. Order matters:
    //   1) URL hash has `session_id=` → render the OAuth callback exchanger
    //   2) `user === undefined` → still probing /auth/me
    //   3) `user === null`      → logged out, show Login
    //   4) `user === object`    → normal app render (below)
    const { user } = useAuth();
    const location = useLocation();
    if (location.hash && location.hash.includes("session_id=")) return <AuthCallback />;
    if (user === undefined) {
        return (
            <div className="min-h-screen bg-meye-bg flex items-center justify-center text-meye-cyan font-mono text-xs uppercase tracking-widest gap-2">
                <Loader2 size={14} className="animate-spin" /> checking session…
            </div>
        );
    }
    if (user === null) return <Login />;

    return <AuthedApp />;
}

function AuthedApp() {
    const { user: authUser } = useAuth();
    const [primary, setPrimary] = useState("time");
    const [sub, setSub] = useState("daily");
    const [tasks, _setTasksRaw] = useState([]);
    // ONE task object per id, on every write path (fetch, SSE, create, PATCH, optimistic).
    const setTasks = useCallback((v) => _setTasksRaw((prev) => normalizeTasksById(typeof v === "function" ? v(prev) : v)), []);
    const tasksRef = useRef([]); // latest tasks for non-React readers (SSE stale-payload detection)
    // Local calendar day — re-renders the Daily view (and its date headers) the moment local midnight passes.
    const [localDay, setLocalDay] = useState(() => todayStr());
    useEffect(() => {
        let t;
        const arm = () => { t = setTimeout(() => { setLocalDay(todayStr()); arm(); }, msUntilNextLocalMidnight() + 500); };
        arm();
        const iv = setInterval(() => setLocalDay((d) => (d === todayStr() ? d : todayStr())), 60 * 1000); // sleep/wake safety
        return () => { clearTimeout(t); clearInterval(iv); };
    }, []);
    useEffect(() => { tasksRef.current = tasks; }, [tasks]);
    const [notes, setNotes] = useState([]);
    const [checklists, setChecklists] = useState([]);
    const [scratchpads, setScratchpads] = useState([]);
    const [glossary, setGlossary] = useState([]);
    const [inventory, setInventory] = useState([]);
    const [bookmarks, setBookmarks] = useState([]);
    const [attachments, setAttachments] = useState([]);
    const [settings, setSettings] = useState(null);
    const [chatLog, setChatLog] = useState([]);
    const [editingTask, setEditingTask] = useState(null);
    const [search, setSearch] = useState("");
    // Semantic search: fires when literal search yields few/no hits — the LLM finds related items
    // by category/theme (e.g. "financial" → accountant tasks, bill payments, bookkeeping).
    const [semantic, setSemantic] = useState({ query: "", task_ids: [], note_ids: [], glossary_ids: [], reasoning: "", loading: false });

    // Stale-build watcher — an installed PWA / long-lived tab keeps running the JS it loaded,
    // so a deploy is invisible until the page reloads. Compare the bundle hash the server
    // currently ships with the one we're running; when they differ, offer a one-tap reload.
    useEffect(() => {
        const running = [...document.scripts].map((s) => s.src).find((src) => /\/static\/js\/main\.[a-f0-9]+\.js/.test(src)) || "";
        if (!running) return;
        let prompted = false;
        const check = async () => {
            if (prompted || document.visibilityState !== "visible") return;
            try {
                const html = await fetch(`/?build=${Date.now()}`, { cache: "no-store" }).then((r) => r.text());
                const m = html.match(/static\/js\/main\.[a-f0-9]+\.js/);
                if (m && !running.includes(m[0])) {
                    prompted = true;
                    toast("A new version of mEYEtasks is ready", {
                        duration: Infinity,
                        action: { label: "Reload", onClick: () => window.location.reload() },
                    });
                }
            } catch { /* offline — try again later */ }
        };
        check();
        const iv = setInterval(check, 5 * 60 * 1000);
        document.addEventListener("visibilitychange", check);
        return () => { clearInterval(iv); document.removeEventListener("visibilitychange", check); };
    }, []);


    // Debounced semantic search — only calls the LLM when literal search is a real term (>=3 chars)
    // and hasn't been requested for this exact query yet. Result is cached in `semantic` state.
    useEffect(() => {
        const q = search.trim();
        if (q.length < 3) {
            if (semantic.query) setSemantic({ query: "", task_ids: [], note_ids: [], glossary_ids: [], reasoning: "", loading: false });
            return;
        }
        if (semantic.query === q) return;
        const handle = setTimeout(async () => {
            setSemantic((s) => ({ ...s, query: q, loading: true }));
            try {
                const res = await api.semanticSearch(q);
                setSemantic({ ...res, loading: false });
            } catch (err) {
                console.warn("semantic search failed", err);
                setSemantic((s) => ({ ...s, loading: false }));
            }
        }, 700);
        return () => clearTimeout(handle);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);
    const [newItemText, setNewItemText] = useState("");
    const [screensaverOn, setScreensaverOn] = useState(true);
    const [missedForTask, setMissedForTask] = useState(null);  // Task open in the MissedOccurrencesDialog
    const [dosePickerTask, setDosePickerTask] = useState(null); // Overdue row with 2+ missed beats awaiting an explicit pick
    const [contextScan, setContextScan] = useState(null);
    const [trainerOn, setTrainerOn] = useState(true);
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [nagTask, setNagTask] = useState(null);
    const [notifOpen, setNotifOpen] = useState(false);
    const [commsHint, setCommsHint] = useState(null);
    const [previewAttachment, setPreviewAttachment] = useState(null);
    const [commsAutoVoice, setCommsAutoVoice] = useState(false);
    const [linkSuggestions, setLinkSuggestions] = useState([]);
    // Force mobile layout via URL: ?mobile=1, ?device=iphone, or ?iphone=1 (in-page phone frame)
    const forceMobile = typeof window !== "undefined" && /(?:^|[?&])(mobile=1|device=iphone|iphone=1)/.test(window.location.search);
    // "Desktop" means a real mouse-driven layout — an actual PC/Mac with a fine pointer at ≥1024px.
    // Touch devices (iPads in landscape, Surface tablets) are treated as mobile so they still get
    // the screensaver and mobile-optimized UI.
    const DESKTOP_MQ = "(min-width: 1024px) and (pointer: fine) and (hover: hover)";
    const [isDesktop, setIsDesktop] = useState(
        forceMobile ? false : (typeof window !== "undefined" ? window.matchMedia(DESKTOP_MQ).matches : false),
    );

    // Track viewport size — desktop hides the screensaver + shows a persistent COMMS rail.
    // Must use the SAME query string as the initial-state media match so both stay in sync.
    useEffect(() => {
        if (typeof window === "undefined" || forceMobile) return;
        const mq = window.matchMedia(DESKTOP_MQ);
        const handler = (e) => setIsDesktop(e.matches);
        mq.addEventListener?.("change", handler);
        return () => mq.removeEventListener?.("change", handler);
    }, [forceMobile]);

    // If accessed with ?iphone=1, render ONLY an iPhone bezel with iframe of the mobile app.
    // Best on webdesk to preview mobile without picking up your phone.
    // (rendered outside App via a HOC — see IphoneFrame at the bottom of this file)


    // On desktop the COMMS rail is always visible, so redirect the mobile COMMS tab away
    useEffect(() => {
        if (isDesktop && primary === "comms") setPrimary("time");
    }, [isDesktop, primary]);

    // Prime browser autoplay on every user gesture until we're actually unlocked.
    // iOS Safari sometimes requires multiple attempts, so we don't use `{once:true}`.
    useEffect(() => {
        const unlock = () => {
            const ok = unlockAudio();
            if (ok) {
                window.removeEventListener("click", unlock);
                window.removeEventListener("keydown", unlock);
                window.removeEventListener("touchstart", unlock);
                window.removeEventListener("touchend", unlock);
            }
        };
        window.addEventListener("click", unlock);
        window.addEventListener("keydown", unlock);
        window.addEventListener("touchstart", unlock);
        window.addEventListener("touchend", unlock);
        // Also re-attempt unlock when the tab regains focus (iOS suspends contexts)
        const onVis = () => {
            if (document.visibilityState === "visible") unlockAudio();
        };
        document.addEventListener("visibilitychange", onVis);
        return () => {
            window.removeEventListener("click", unlock);
            window.removeEventListener("keydown", unlock);
            window.removeEventListener("touchstart", unlock);
            window.removeEventListener("touchend", unlock);
            document.removeEventListener("visibilitychange", onVis);
        };
    }, []);

    // ---- Live sync via SSE ------------------------------------------------
    // Opens one long-lived EventSource to /api/events. Any mutation the SAME user performs on
    // any other device (or the AI mission endpoint, or the nag scheduler) fans back to every
    // open tab as `{type:"tasks.upserted"|"tasks.deleted"|...}`. We patch the corresponding
    // state list in place so the UI updates without a full refetch. Native EventSource handles
    // reconnection automatically; on repeated failures we fall back to a manual reopen with
    // exponential backoff.
    useEffect(() => {
        if (typeof window === "undefined" || typeof EventSource === "undefined") return;
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/events`;
        let es = null;
        let cancelled = false;
        let backoff = 1000;
        // Map collection → [state, setState] pair so the switch below stays compact
        const stateMap = {
            tasks: setTasks,
            notes: setNotes,
            checklists: setChecklists,
            scratchpads: setScratchpads,
            glossary: setGlossary,
            inventory: setInventory,
            bookmarks: setBookmarks,
        };
        const upsertInto = (setter, doc) => {
            if (!doc || !doc.id) return;
            setter((prev) => applyUpsert(prev, doc));
        };
        const deleteFrom = (setter, id) => {
            if (!id) return;
            setter((prev) => prev.filter((x) => x.id !== id));
        };
        const connect = () => {
            if (cancelled) return;
            try {
                // withCredentials sends the session_token cookie so the SSE endpoint can auth.
                es = new EventSource(url, { withCredentials: true });
            } catch {
                setTimeout(connect, backoff);
                backoff = Math.min(backoff * 2, 30000);
                return;
            }
            es.onopen = () => { backoff = 1000; };
            es.onmessage = (ev) => {
                try {
                    const msg = JSON.parse(ev.data);
                    if (!msg || !msg.type) return;
                    if (msg.type === "hello") return;
                    const [collection, action] = msg.type.split(".");
                    if (collection === "tasks" && action === "upserted" && msg.data) {
                        const held = tasksRef.current.find((x) => x.id === msg.id);
                        const stale = isStaleRevision(held, msg.data);
                        if (stale || tracedIds().has(msg.id)) logDiag({ kind: stale ? "sse.STALE" : "sse", task_id: msg.id, message: `${msg.type}${stale ? ` — payload rev ${msg.data.rev} < held rev ${held.rev} — IGNORED` : ""}`, state: stateSummary(msg.data) });
                        else logDiag({ kind: "sse", message: msg.type, task_id: msg.id, state: { rev: msg.data.rev ?? null } });
                        if (stale) return; // never let an older revision overwrite newer local state
                    } else logDiag({ kind: "sse", message: msg.type, task_id: msg.id });
                    const setter = stateMap[collection];
                    if (!setter) return;
                    if (action === "upserted") upsertInto(setter, msg.data);
                    else if (action === "deleted") deleteFrom(setter, msg.id);
                } catch {/* ignore malformed */}
            };
            es.onerror = () => {
                logDiag({ kind: "sse", message: `error readyState=${es?.readyState}` });
                // Native EventSource will retry on its own; if it CLOSED, reopen manually.
                if (es && es.readyState === 2 /* CLOSED */) {
                    try { es.close(); } catch { /* ignore */ }
                    es = null;
                    setTimeout(connect, backoff);
                    backoff = Math.min(backoff * 2, 30000);
                }
            };
        };
        connect();
        return () => {
            cancelled = true;
            if (es) { try { es.close(); } catch { /* ignore */ } }
        };
    }, []);

    // If any TTS attempt gets blocked, surface a one-tap "enable voice" toast.
    useEffect(() => {
        return onVoiceBlocked(() => {
            toast.message("🔊 Tap to enable voice", {
                description: "Browser blocked the trainer's voice — click to unmute.",
                action: {
                    label: "Enable",
                    onClick: () => retryBlockedSpeech(),
                },
                duration: 12000,
            });
        });
    }, []);

    // When app opens from a push-notification click (?nag=<id>) — show the nag overlay
    useEffect(() => {
        if (!tasks.length) return;
        const params = new URLSearchParams(window.location.search);
        const nagId = params.get("nag");
        const nagOcc = params.get("occ");
        if (nagId) {
            const t = tasks.find((x) => x.id === nagId);
            if (t) setNagTask(nagOcc ? { ...t, _occKey: nagOcc } : t);
            // remove the param so a reload doesn't re-trigger
            window.history.replaceState({}, "", window.location.pathname);
        }
    }, [tasks.length]);

    // idle timer -> re-enter screensaver
    useEffect(() => {
        if (screensaverOn) return;
        let idleTimer;
        const reset = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(() => setScreensaverOn(true), 60_000);
        };
        const events = ["mousemove", "mousedown", "touchstart", "keydown", "scroll"];
        events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
        reset();
        return () => {
            clearTimeout(idleTimer);
            events.forEach((e) => window.removeEventListener(e, reset));
        };
    }, [screensaverOn]);

    // geofence polling every 5 min (server proxies ipapi.co to avoid CORS)
    useEffect(() => {
        let cancelled = false;
        const check = async () => {
            try {
                const data = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/geo/current`).then((r) => r.json());
                const city = data.city;
                if (!city || cancelled) return;
                const gres = await api.geofence(city);
                if (cancelled) return;
                if (gres.changed && gres.matched_terms?.length) {
                    toast.info(`arrived in ${city} — ${gres.surfaced_task_ids.length} matching task(s)`);
                    setContextScan(gres);
                    // If TTS is on, let the trainer speak the arrival aloud.
                    if (gres.spoken && settings?.comms_tts_enabled) {
                        try {
                            const { speak } = await import("@/lib/tts");
                            speak(gres.spoken, { voiceName: settings?.comms_tts_voice || "nova" });
                        } catch {
                            /* ignore */
                        }
                    }
                }
            } catch {}
        };
        check();
        const id = setInterval(check, 5 * 60 * 1000);
        return () => {
            cancelled = true;
            clearInterval(id);
        };
    }, [settings?.comms_tts_enabled, settings?.comms_tts_voice]);

    const loadAll = useCallback(async () => {
        try {
            const [t, n, c, s, g, inv, bm, a, st] = await Promise.all([
                api.listTasks(),
                api.listNotes(),
                api.listChecklists(),
                api.listScratchpads(),
                api.listGlossary(),
                api.listInventory(),
                api.listBookmarks(),
                api.listAttachments(),
                api.getSettings(),
            ]);
            // TEMPORARY tracing (5. NEXT TASK FETCH): persisted state of recently completed occurrences.
            for (const id of tracedIds()) logDiag({ kind: "fetch", task_id: id, message: "GET /tasks", state: stateSummary(t.find((x) => x.id === id)) });
            setTasks(t);
            setNotes(n);
            setChecklists(c);
            setScratchpads(s);
            setGlossary(g);
            setInventory(inv);
            setBookmarks(bm);
            setAttachments(a);
            // Cold-start guard: if the server-persisted pending_training was Skip/Dismissed by
            // the user within the last 7 days (see /app/frontend/src/lib/trainingSkip.js), silently
            // clear it BEFORE we let the banner render. This is what fixes the "won't go away"
            // bug — the AI persisted a question that survived across sessions but the user has
            // already told us they don't want to answer.
            if (st?.pending_training && isTrainingSkipped(st.pending_training)) {
                setSettings({ ...st, pending_training: null });
                api.updateSettings({ pending_training: null }).catch((e) => console.warn("re-clear skipped training failed", e));
            } else {
                setSettings(st);
            }
        } catch (e) {
            console.error(e);
        }
    }, []);

    useEffect(() => {
        loadAll();
    }, [loadAll]);

    // Morning Briefing — once per day between 5-11am the assistant speaks a short summary of
    // overdue items + the next appointment. Stored in localStorage so it doesn't re-fire on
    // subsequent reloads / navigations. Requires TTS to be enabled AND tasks+settings loaded.
    const briefedRef = useRef(false);
    useEffect(() => {
        if (briefedRef.current) return;
        if (!tasks?.length || !settings) return;
        if (!settings.comms_tts_enabled) return;
        const now = new Date();
        const hour = now.getHours();
        if (hour < 5 || hour >= 11) return;
        const stamp = `${now.getFullYear()}-${now.getMonth()+1}-${now.getDate()}`;
        try {
            if (localStorage.getItem("meye_briefed_on") === stamp) {
                briefedRef.current = true;
                return;
            }
        } catch { /* localStorage may be blocked in some contexts */ }

        const nowMs = Date.now();
        const active = tasks.filter((t) => !t.archived && !t.completed && (!t.snoozed_until || t.snoozed_until < nowMs));
        const overdueCount = active.filter((t) => t.date && t.type !== "recurring" && daysDiff(t.date) < 0).length;
        // Next appointment = the soonest dated+timed item (today OR upcoming) that hasn't started yet.
        const timed = active
            .filter((t) => t.date && t.time)
            .map((t) => {
                const [Y, M, D] = t.date.split("-").map(Number);
                const [h, m] = t.time.split(":").map(Number);
                const when = new Date(Y, M - 1, D, h, m).getTime();
                return { t, when };
            })
            .filter(({ when }) => when >= nowMs)
            .sort((a, b) => a.when - b.when);
        const next = timed[0];

        if (overdueCount === 0 && !next) return; // nothing worth mentioning

        const name = settings.user_name || "trainer";
        const assistant = settings.assistant_name || "mEYEtrainer";
        const bits = [`Good morning ${name}.`];
        if (overdueCount === 1) bits.push("You have one overdue item.");
        else if (overdueCount > 1) bits.push(`You have ${overdueCount} overdue items.`);
        if (next) {
            const fmt = new Date(next.when).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }).toLowerCase();
            const isToday = daysDiff(next.t.date) === 0;
            const day = isToday ? "today" : "tomorrow";
            bits.push(`Next up at ${fmt} ${day}: ${next.t.text}.`);
        }
        const line = bits.join(" ");
        briefedRef.current = true;
        try { localStorage.setItem("meye_briefed_on", stamp); } catch { /* ignore */ }

        // Speak — small delay lets the initial screensaver animation start first so the greeting
        // arrives once the user's eyes are on the screen.
        setTimeout(() => {
            import("@/lib/tts")
                .then(({ speak }) => speak(line, { voiceName: settings.comms_tts_voice || "nova" }))
                .catch(() => { /* TTS module unavailable */ });
            // Also drop a soft toast so silent users still see the briefing.
            toast(`${assistant}: ${line}`, { duration: 8000 });
        }, 900);
    }, [tasks, settings]);

    const refreshAttachments = async () => {
        try {
            setAttachments(await api.listAttachments());
        } catch (e) {
            console.error(e);
        }
    };

    // ---- when primary changes, reset sub to first tab of that primary ----
    useEffect(() => {
        const first = SUB_TABS[primary]?.[0]?.id;
        if (first && !SUB_TABS[primary]?.some((t) => t.id === sub)) {
            setSub(first);
        }
    }, [primary]);

    // ---- task actions ----
    // The SSE hub echoes our own POST back as `tasks.upserted` — often BEFORE the HTTP response
    // resolves. A naive `[task, ...prev]` then held the same id twice and rendered two rows.
    const upsertTaskLocal = (task) => {
        if (!task?.id) return;
        setTasks((prev) => (prev.some((t) => t.id === task.id) ? prev.map((t) => (t.id === task.id ? task : t)) : [task, ...prev]));
    };

    const toggleTask = async (task, occurrenceIso = null, view = "unknown") => {
        // TEMPORARY tracing (1. USER CLICKS COMPLETE): identity = task_id + occurrenceIso, state BEFORE.
        const traceId = api.newTraceId();
        logDiag({ kind: "complete.click", task_id: task.id, message: `"${String(task.text || "").slice(0, 60)}"`, iso: occurrenceIso || null, view, trace_id: traceId,
            state: { recurring: task.type === "recurring" && !!task.interval, ...stateSummary(task) } });
        // Recurring tasks: every checkbox click is a per-occurrence check-in for the beat the
        // row represents (`occurrenceIso` — Today, Tomorrow, a Weekly/Monthly day, the editor's
        // occurrence). Falls back to today's local ISO only when the caller has no beat context. `completed_at` is intentionally NOT touched
        // on recurring toggles — it now serves as a FROZEN legacy baseline (any beat whose
        // scheduled ms is ≤ the pre-refactor `completed_at` value is assumed done historically,
        // see getMissedOccurrences in utils.js). Overwriting it every tap would silently mark
        // every past unresolved beat as "done" and defeat the whole carry-forward system.
        if (task.type === "recurring") {
            const prevCompletions = task.completions || [];
            const prevCompletionTimes = task.completion_times || {};
            let todayIso = /^\d{4}-\d{2}-\d{2}$/.test(occurrenceIso || "") ? occurrenceIso : todayStr();
            // Guard: a key that is not an obligation of this series (e.g. today's date on a weekly
            // task whose beat was yesterday) would persist a no-op. With exactly ONE outstanding
            // missed beat that beat is the unambiguous target; with 2+ we refuse — the caller
            // must go through the Overdue Dose Picker so nothing is implicitly chosen.
            const validKeys = new Set(getOccurrencesOnIso(task, todayIso).map((o) => o.key));
            if (!validKeys.has(todayIso) && !effectiveOverrides(task).some((ov) => ov.from === todayIso)) {
                const misses = getMissedOccurrences(task);
                if (misses.length === 1) todayIso = misses[0];
                else if (misses.length > 1) { toast.error(`${task.text.slice(0, 40)} has ${misses.length} missed doses — pick which one from the Overdue row`); return; }
                else { toast.error(`${task.text.slice(0, 40)} has no occurrence to check in for ${formatDate(todayIso)}`); return; }
            }
            // FUTURE-WRITE GUARD (client mirror of the server invariant): the effective day of this
            // source obligation must not be after local today. Tomorrow/Weekly/Monthly/editor rows
            // for future beats can be edited or rescheduled, never completed early.
            if (isFutureObligation(task, todayIso)) {
                logDiag({ kind: "complete.error", task_id: task.id, iso: todayIso, view, trace_id: traceId, status: 0, message: `blocked client-side: future obligation (effective ${effectiveDisplayIso(task, todayIso)})` });
                toast.error(`${task.text.slice(0, 40)} — ${formatDate(effectiveDisplayIso(task, todayIso))} hasn't come yet; it can't be checked in early`);
                return false;
            }
            const alreadyDone = prevCompletions.includes(todayIso);
            logDiag({ kind: "complete.request", task_id: task.id, iso: todayIso, view, trace_id: traceId, message: `${alreadyDone ? "no-op (already done)" : "POST"} /occurrences/${todayIso}/complete${todayIso !== (occurrenceIso || null) ? ` (resolved from ${occurrenceIso || "none"})` : ""}` });
            rememberTraced(task.id, todayIso, task.text);
            const optimistic = {
                ...task,
                completions: alreadyDone ? prevCompletions : [...prevCompletions, todayIso],
                completion_times: { ...prevCompletionTimes, [todayIso]: Date.now() },
            };
            setTasks((prev) => prev.map((t) => (t.id === task.id ? optimistic : t)));
            try {
                // ATOMIC per-occurrence write ($addToSet) — never PATCHes the whole array, so two
                // devices checking different beats cannot overwrite each other.
                const upd = await api.completeOccurrence(task.id, todayIso, traceId);
                logDiag({ kind: "complete.response", task_id: task.id, iso: todayIso, trace_id: traceId, state: stateSummary(upd), message: upd.completions?.includes(todayIso) ? "persisted" : "RESPONSE LACKS KEY" });
                setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
            } catch (err) {
                logDiag({ kind: "complete.error", task_id: task.id, iso: todayIso, trace_id: traceId, status: err?.response?.status, message: String(err?.response?.data?.detail || err?.message || "").slice(0, 160) });
                setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
                toast.error(err?.response?.data?.detail || "Couldn't check in — put it back");
                return false;
            }
            const when = todayIso === todayStr() ? "" : ` for ${formatDate(todayIso)}`;
            toast.success(`${task.text.slice(0, 40)} — checked in${when}`, {
                action: {
                    label: "Undo",
                    onClick: async () => {
                        const reverted = await api.reopenOccurrence(task.id, todayIso);
                        setTasks((prev) => prev.map((t) => (t.id === reverted.id ? reverted : t)));
                    },
                },
                duration: 6000,
            });
            return;
        }
        const wasCompleted = !!task.completed;
        // OPTIMISTIC UPDATE — flip completed locally BEFORE the server round-trip so the row
        // vanishes from Today/Overdue instantly. Without this, a right-swipe leaves the card
        // sitting on-screen for however long the API takes (~200-800ms), reading as "stuck on
        // completed". If the server rejects, we revert.
        const nowMs = Date.now();
        const optimistic = { ...task, completed: !wasCompleted, completed_at: !wasCompleted ? nowMs : null };
        setTasks((prev) => prev.map((t) => (t.id === task.id ? optimistic : t)));
        logDiag({ kind: "complete.request", task_id: task.id, iso: task.date || null, view, trace_id: traceId, message: `PATCH completed=${!wasCompleted} completed_at=${!wasCompleted ? nowMs : null}` });
        rememberTraced(task.id, task.date || null, task.text);
        try {
            const upd = await api.updateTask(task.id, {
                completed: !wasCompleted,
                completed_at: !wasCompleted ? nowMs : null,
            }, traceId);
            logDiag({ kind: "complete.response", task_id: task.id, trace_id: traceId, state: stateSummary(upd), message: upd.completed === !wasCompleted ? "persisted" : "RESPONSE MISMATCH" });
            setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
        } catch (err) {
            logDiag({ kind: "complete.error", task_id: task.id, trace_id: traceId, status: err?.response?.status, message: String(err?.response?.data?.detail || err?.message || "").slice(0, 160) });
            // Server rejected — put the task back where it was.
            setTasks((prev) => prev.map((t) => (t.id === task.id ? task : t)));
            toast.error("Couldn't save — put it back");
            return;
        }
        // Only offer Undo on the check-off direction — un-completing already IS an undo.
        if (!wasCompleted) {
            toast.success(`Marked done — ${task.text.slice(0, 40)}`, {
                action: {
                    label: "Undo",
                    onClick: async () => {
                        const reverted = await api.updateTask(task.id, {
                            completed: false,
                            completed_at: null,
                        });
                        setTasks((prev) => prev.map((t) => (t.id === reverted.id ? reverted : t)));
                    },
                },
                duration: 6000,
            });
        }
    };
    const archiveTask = async (task) => {
        if (task.archived) {
            await api.deleteTask(task.id);
            setTasks((prev) => prev.filter((t) => t.id !== task.id));
        } else {
            const upd = await api.updateTask(task.id, { archived: true, archived_at: Date.now() });
            setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
            toast.success("Archived");
        }
    };
    const snoozeTask = async (task, until) => {
        const upd = await api.updateTask(task.id, { snoozed_until: until });
        setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
        toast.success("Snoozed");
    };
    const saveTask = async (id, patch) => {
        const upd = await api.updateTask(id, patch);
        setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
    };
    const deleteTask = async (id) => {
        await api.deleteTask(id);
        setTasks((prev) => prev.filter((t) => t.id !== id));
    };

    /** Bumps one occurrence of a recurring task to `toIso`. On collision the backend returns
     *  409 — we surface it as a toast so the user can pick a different day. */
    // Occurrence-level move: writes an instance_override {from, to[, to_time]} — the series
    // anchor/interval are never touched. Returns true iff persisted (TaskEditor chains on it).
    const postponeTask = async (task, fromIso, toIso, toTime = null, reason = "postpone") => {
        try {
            const upd = await api.postponeTask(task.id, fromIso, toIso, toTime, reason);
            setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
            toast(`Moved ${formatDate(fromIso)} → ${formatDate(toIso)} — series unchanged`, { duration: 3500 });
            return true;
        } catch (e) {
            const msg = e?.response?.data?.detail || "Couldn't move this occurrence — try another day";
            toast(msg, { duration: 4000 });
            return false;
        }
    };
    /** Edit from an Overdue surface (list row or Screensaver card) — identical decision on both:
     *  0 outstanding → nothing opens (refresh) · 1 → editor on that exact source key · 2+ → picker. */
    const openOverdueEdit = (task) => {
        const r = overdueEditTarget(task);
        if (r.kind === "none") { toast.error("No outstanding occurrence to edit — list refreshed"); loadAll(); return; }
        if (r.kind === "pick") { setDosePickerTask({ ...r.task, _pickMode: "edit" }); return; }
        setEditingTask(r.task);
    };
    const undoPostpone = async (task, fromIso) => {
        try {
            const upd = await api.unpostponeTask(task.id, fromIso);
            setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
            toast("Move undone", { duration: 2500 });
        } catch (e) {
            toast("Couldn't undo — please retry", { duration: 3000 });
        }
    };
    /** Snoozes a task until "later" — the label + target both shift based on the current
     *  wall-clock hour so the affordance reads naturally in context:
     *
     *    now < 5pm      → "later tonight" · snooze to 8pm TODAY
     *    5pm ≤ now < 9pm → "later tonight" · snooze to 9pm TODAY (or +90min, whichever is later)
     *    now ≥ 9pm      → "tomorrow morning" · snooze to 8am TOMORROW
     *
     *  The old logic always used 8pm today, which meant a 9am task viewed at 9:03pm would try
     *  to snooze to a moment that already passed — reading as broken ("later tonight" for a task
     *  at 9pm doesn't parse when it IS tonight already). See snoozeChipFor(). */
    const snoozeUntilTonight = async (task) => {
        const now = new Date();
        const hour = now.getHours();
        const target = new Date(now);
        if (hour < 17) {
            target.setHours(20, 0, 0, 0);          // 8pm today
        } else if (hour < 21) {
            target.setTime(now.getTime() + 90 * 60 * 1000); // now + 90min
        } else {
            target.setDate(target.getDate() + 1);
            target.setHours(8, 0, 0, 0);           // 8am tomorrow
        }
        try {
            let upd;
            if (task.type === "recurring" && task.interval) {
                // Occurrence-level: snooze ONLY the missed beats this Overdue row represents.
                // Today's pending beat, future beats and the series itself stay untouched.
                // Never include today's own beat — snoozing prior misses must not hide today.
                const isos = (task._missedOccurrences || []).filter((iso) => iso !== todayStr());
                if (!isos.length) { toast("Nothing before today to snooze", { duration: 2500 }); return; }
                // ONE atomic bulk write (dotted $set per SOURCE key) — never the legacy whole-map PATCH.
                upd = await api.snoozeOccurrencesBulk(task.id, isos, target.getTime());
            } else {
                upd = await api.snoozeTask(task.id, target.getTime());
            }
            setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)));
            const isTomorrow = target.getDate() !== now.getDate();
            const timeStr = target.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
            toast(`Snoozed until ${isTomorrow ? "tomorrow " : ""}${timeStr}`, { duration: 3000 });
        } catch (e) {
            toast("Couldn't snooze — please retry", { duration: 3000 });
        }
    };
    /** Human label + a11y title for the snooze chip. Kept alongside `snoozeUntilTonight` so
     *  the two never drift out of sync. */
    const snoozeChipFor = () => {
        const hour = new Date().getHours();
        if (hour < 17) return { label: "later tonight", title: "Snooze until 8 pm tonight" };
        if (hour < 21) return { label: "in ~90 min", title: "Snooze 90 minutes" };
        return { label: "tomorrow AM", title: "Snooze until 8 am tomorrow" };
    };
    const updateNote = async (id, patch) => {
        const upd = await api.updateNote(id, patch);
        setNotes((prev) => prev.map((n) => (n.id === upd.id ? upd : n)));
    };
    const deleteNote = async (id) => {
        await api.deleteNote(id);
        setNotes((prev) => prev.filter((n) => n.id !== id));
    };
    const updateChecklist = async (id, patch) => {
        const upd = await api.updateChecklist(id, patch);
        setChecklists((prev) => prev.map((c) => (c.id === upd.id ? upd : c)));
    };
    const deleteChecklist = async (id) => {
        await api.deleteChecklist(id);
        setChecklists((prev) => prev.filter((c) => c.id !== id));
    };
    const deleteGlossary = async (id) => {
        await api.deleteGlossary(id);
        setGlossary((prev) => prev.filter((g) => g.id !== id));
    };
    const deleteScratchpad = async (id) => {
        await api.deleteScratchpad(id);
        setScratchpads((prev) => prev.filter((s) => s.id !== id));
    };
    const createInventory = async (data) => {
        try {
            const inv = await api.createInventory(data);
            setInventory((p) => [inv, ...p]);
            toast.success("Item logged");
        } catch (e) {
            toast.error("Failed to log item");
        }
    };
    const updateInventory = async (id, patch) => {
        const upd = await api.updateInventory(id, patch);
        setInventory((prev) => prev.map((i) => (i.id === upd.id ? upd : i)));
    };
    const deleteInventory = async (id) => {
        await api.deleteInventory(id);
        setInventory((prev) => prev.filter((i) => i.id !== id));
    };

    // DND — pauses server-side nagging until epoch ms in the future
    const dndActive = !!(settings?.dnd_until && settings.dnd_until > Date.now());
    const toggleDnd = async () => {
        try {
            if (dndActive) {
                const upd = await api.updateSettings({ dnd_until: null });
                setSettings(upd);
                toast.success("Do Not Disturb off");
            } else {
                // default: silence nags for 2 hours
                const until = Date.now() + 2 * 60 * 60 * 1000;
                const upd = await api.updateSettings({ dnd_until: until });
                setSettings(upd);
                toast.success("Do Not Disturb · 2h");
            }
        } catch {
            toast.error("Failed");
        }
    };

    // Quick-add
    const addForCurrentView = async () => {
        const t = newItemText.trim();
        if (!t) return;
        try {
            if (sub === "notes") {
                const n = await api.createNote({ text: t });
                setNotes((p) => [n, ...p]);
            } else if (sub === "scratchpad") {
                const s = await api.createScratchpad({ text: t });
                setScratchpads((p) => [s, ...p]);
            } else if (sub === "checklists") {
                const c = await api.createChecklist({ title: t, items: [] });
                setChecklists((p) => [c, ...p]);
            } else if (["daily", "events", "recurring", "weekly", "monthly"].includes(sub)) {
                const client_request_id = api.newRequestId(); // minted once per submit; retries reuse it
                const task = await api.createTask({
                    client_request_id,
                    text: t,
                    type: sub === "daily" ? "scheduled" : "todo",
                    date: sub === "daily" ? todayStr() : null,
                    source: "inline_add",
                });
                upsertTaskLocal(task);
            }
            setNewItemText("");
        } catch (e) {
            console.error(e);
            toast.error("Add failed");
        }
    };

    const onAiResult = async (res) => {
        // If the AI is re-asking a training question the user already Skip/Dismissed within the
        // last 7 days (checked against both context slug AND normalised question text — see
        // trainingSkip.js), silently clear it server-side so the banner never appears.
        try {
            if (res?.training_question) {
                const pt = { question: res.training_question, context: res.training_context };
                if (isTrainingSkipped(pt)) {
                    await api.updateSettings({ pending_training: null });
                }
            }
        } catch (_) { /* best effort */ }
        if (res?.created?.length || res?.training_question !== undefined) await loadAll();
    };
    const onSearchIntent = (q) => setSearch(q);
    const onLocationIntent = async (place) => {
        try {
            const res = await api.geofence(place);
            if (res.matched_terms?.length) {
                toast.success(`context scan: ${res.surfaced_task_ids.length} task(s) match`);
                setContextScan(res);
                setSearch(place);
            } else {
                toast.info(`no glossary match for "${place}"`);
            }
        } catch (e) {
            toast.error("Context scan failed");
        }
    };

    // filtering
    const activeTasks = useMemo(() => {
        const now = Date.now();
        // Recurring cadences are never hidden wholesale by a task-level snooze — their snoozes
        // are per-occurrence (`occurrence_snoozes`) and applied inside each list's filter.
        return tasks.filter((t) => !t.archived && !isTaskSnoozed(t, now));
    }, [tasks, localDay]);
    // Normalize both sides for whitespace + punctuation so "homedepot" matches "Home Depot",
    // "pjs" matches "P.J.'s", and "wifi" matches "Wi-Fi". Only alphanumerics survive.
    const normalize = (t) => String(t || "").toLowerCase().replace(/[^a-z0-9]+/g, "");
    const s = search.trim().toLowerCase();
    const sNormal = normalize(search);
    const matches = (txt) => {
        if (!s) return true;
        const raw = String(txt || "").toLowerCase();
        if (raw.includes(s)) return true;
        // Fallback: strip whitespace + punctuation on both sides. Keep the raw check first so
        // multi-word queries like "pay home" still work naturally.
        return sNormal.length > 0 && normalize(raw).includes(sNormal);
    };

    // Overdue lives above Today on the Daily view so the user sees stale items first. This now
    // includes BOTH non-recurring past-due AND recurring items whose current cycle is missed
    // (time-passed today unchecked or prior occurrence unchecked) — matches the phone
    // screensaver's OVERDUE window so double-tapping there lands on the same items here.
    const overdueTasks = useMemo(
        () => {
            // Treat malformed `type=recurring, interval=null` rows as PLAIN scheduled tasks —
            // they were created by an old AI turn that couldn't map the user's range onto our
            // {1,7,14,30,365} enum, so they carry a real anchor date+time but no cadence. We
            // should still surface them as Overdue when the anchor day is in the past.
            const isScheduled = (t) => t.type !== "recurring" || !t.interval;
            const startOfToday = new Date(); startOfToday.setHours(0, 0, 0, 0);
            const startMs = startOfToday.getTime();
            const cycleBoundaryFor = (t) => {
                if (t.type !== "recurring" || !t.time) return startMs;
                const [hh, mm] = t.time.split(":").map(Number);
                const d = new Date(); d.setHours(hh, mm, 0, 0);
                return d.getTime();
            };
            // Occurrence-level check-in takes precedence over the cycle-boundary heuristic:
            // if today's ISO is in `completions`, the beat is done regardless of any timestamp.
            const todayIso = todayStr();
            const checkedInToday = (t) =>
                t.type === "recurring" && (
                    (t.completions || []).includes(todayIso) ||
                    (t.completed_at || 0) >= cycleBoundaryFor(t)
                );
            const nonRecurring = activeTasks
                .filter((t) => !t.completed && t.date && daysDiff(t.date) < 0 && isScheduled(t));
            // Same-day time-passed check: a 7:45am task at 7:46am should show in Overdue too
            // (parity with recurring's missed-beat handling in isRecurringMissed). Task still
            // shows in Today via the todayTasks filter below — user sees both cards.
            const timePassed = activeTasks.filter((t) => {
                if (t.completed || !isScheduled(t)) return false;
                if (!t.date || !t.time || daysDiff(t.date) !== 0) return false;
                if (checkedInToday(t)) return false;
                try {
                    const [hh, mm] = t.time.split(":").map(Number);
                    const sched = new Date(); sched.setHours(hh, mm, 0, 0);
                    return sched.getTime() <= Date.now();
                } catch { return false; }
            });
            const missedRecurring = activeTasks
                .filter((t) => t.type === "recurring" && t.interval)
                .map((t) => {
                    // Per-occurrence carry-forward — one grouped row per recurring task showing
                    // ALL missed beats, oldest first. getMissedOccurrences already skips today's
                    // beat when today's ISO is in `completions[]`, so the row only stays when at
                    // least one PRIOR beat is still outstanding. Completing today therefore
                    // clears today's beat WITHOUT hiding earlier misses (the whole point of the
                    // carry-forward model).
                    const misses = getMissedOccurrences(t);
                    if (!misses.length) return null;
                    // The editor opened from this row acts on ONE obligation: its SOURCE key
                    // (`_occKey`) + where it currently sits (`_displayIso`). With several missed
                    // beats the most recent one is the row's subject; older ones stay piled.
                    const key = misses[misses.length - 1];
                    const ov = effectiveOverrides(t).find((o) => o.from === key);
                    return { ...t, _missedOccurrences: misses, _missedCount: misses.length, _oldestMissedIso: misses[0], _occKey: key, _displayIso: ov?.to || key, _movedFrom: ov ? key : null, time: ov?.to_time || t.time };
                })
                .filter(Boolean);
            return [...nonRecurring, ...timePassed, ...missedRecurring]
                .filter((t) => matches(t.text))
                .sort((a, b) => (daysDiff(a.date) ?? 0) - (daysDiff(b.date) ?? 0));
        },
        [activeTasks, s],
    );
    // Today's items — dated-for-today OR recurring-that-occurs-today (matches Screensaver logic).
    // ONE LOCATION PER OBLIGATION: an occurrence that is already overdue (timed beat past its
    // time) lives in Overdue only. A date-only beat is "today" for its whole local day and only
    // becomes overdue once the local date rolls over (see utils.occurrenceDueMs).
    const todayTasks = useMemo(() => {
        const today = todayStr();
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const startMs = startOfToday.getTime();
        const nowMs = Date.now();
        return activeTasks
            .flatMap((t) => {
                // Recurring: one ROW PER OBLIGATION on today. A beat moved here from another day
                // keeps its own identity (`_occKey` = source ISO) so it is completed / snoozed
                // independently of today's native beat and never collapses into it.
                if (t.type === "recurring" && t.interval) {
                    const overdueKeys = new Set(getMissedOccurrences(t));
                    return getOccurrencesOnIso(t, today)
                        .filter((occ) => {
                            if ((t.completions || []).includes(occ.key)) return false;
                            if (isOccurrenceSnoozed(t, occ.key)) return false;
                            if (overdueKeys.has(occ.key)) return false; // already in Overdue — never in both
                            if (occ.movedFrom) return true; // moved beats are never legacy-baselined
                            const boundary = (() => {
                                if (!occ.time) return startMs;
                                const [hh, mm] = occ.time.split(":").map(Number);
                                const d = new Date(); d.setHours(hh, mm, 0, 0);
                                return d.getTime();
                            })();
                            return (t.completed_at || 0) < boundary;
                        })
                        .map((occ) => ({ ...t, time: occ.time, _occKey: occ.key, _displayIso: occ.displayIso, _movedFrom: occ.movedFrom }));
                }
                if (t.completed) return [];
                if (t.date !== today) return [];
                if (t.time && occurrenceDueMs(today, t.time) <= nowMs) return []; // timed one-shot past its time → Overdue only
                return [t];
            })
            .filter((t) => matches(t.text))
            .sort((a, b) => {
                const aH = a.time ? 0 : 1, bH = b.time ? 0 : 1;
                if (aH !== bH) return aH - bH;
                return (a.time || "").localeCompare(b.time || "");
            });
    }, [activeTasks, s]);
    // Inbox — quick-capture dumps land here for manual sorting.
    const inboxTasks = useMemo(
        () =>
            activeTasks
                .filter((t) => !t.completed && (t.tags || []).includes("inbox"))
                .filter((t) => matches(t.text))
                .sort((a, b) => (b.created_at || 0) - (a.created_at || 0)),
        [activeTasks, s],
    );
    // Backwards-compat: existing `dailyTasks` still exposes the combined set for anything
    // that reads it elsewhere (e.g. desktop panels).
    const dailyTasks = useMemo(() => [...overdueTasks, ...todayTasks], [overdueTasks, todayTasks]);

    /** Tomorrow-only list — same rules as todayTasks but on tomorrow's ISO. Kept separate so
     *  the Daily view can show a distinct "Tomorrow" section between Today and Someday.
     *  Recurring tasks that hit tomorrow's cycle surface here; malformed recurring rows fall
     *  through to the scheduled logic just like the todayTasks filter. */
    const tomorrowTasks = useMemo(() => {
        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const iso = `${tomorrow.getFullYear()}-${String(tomorrow.getMonth() + 1).padStart(2, "0")}-${String(tomorrow.getDate()).padStart(2, "0")}`;
        // Start-of-tomorrow / end-of-tomorrow ms bounds — used to check if a recurring task's
        // check-in already lands on tomorrow's beat so we don't preview a "done" one.
        const startOfTomorrow = new Date(tomorrow); startOfTomorrow.setHours(0, 0, 0, 0);
        const endOfTomorrow = new Date(tomorrow); endOfTomorrow.setHours(23, 59, 59, 999);
        const startMs = startOfTomorrow.getTime();
        const endMs = endOfTomorrow.getTime();
        return activeTasks
            .flatMap((t) => {
                if (t.type === "recurring" && t.interval) {
                    return getOccurrencesOnIso(t, iso)
                        .filter((occ) => {
                            if ((t.completions || []).includes(occ.key)) return false; // done early
                            if (isOccurrenceSnoozed(t, occ.key)) return false;
                            if (occ.movedFrom) return true;
                            const ca = t.completed_at || 0;
                            return !(ca >= startMs && ca <= endMs); // legacy: checked in for tomorrow? hide
                        })
                        .map((occ) => ({ ...t, time: occ.time, _occKey: occ.key, _displayIso: occ.displayIso, _movedFrom: occ.movedFrom }));
                }
                if (t.completed) return [];
                return t.date === iso ? [t] : [];
            })
            .filter((t) => matches(t.text))
            .sort((a, b) => {
                const aH = a.time ? 0 : 1, bH = b.time ? 0 : 1;
                if (aH !== bH) return aH - bH;
                return (a.time || "").localeCompare(b.time || "");
            });
    }, [activeTasks, s]);
    // Anything without a date lives under "Someday" — sort by priority (high → med → low),
    // then by newest-first within each priority bucket. Legacy tasks with null priority read
    // as "med" so they stay in the natural middle.
    const somedayTasks = useMemo(
        () => {
            const rank = { high: 0, med: 1, low: 2 };
            return activeTasks
                .filter((t) => !t.completed && !t.date && t.type !== "recurring" && !(t.tags || []).includes("inbox"))
                .filter((t) => matches(t.text))
                .sort((a, b) => {
                    const ra = rank[a.priority || "med"] ?? 1;
                    const rb = rank[b.priority || "med"] ?? 1;
                    if (ra !== rb) return ra - rb;
                    return (b.created_at || 0) - (a.created_at || 0);
                });
        },
        [activeTasks, s],
    );
    // Recently completed non-recurring tasks — surface a rescue list right on the Daily view
    // so an accidental swipe-complete is trivially reversible without hunting for Tools > Archive.
    // Rolling window: everything checked off in the last 7 days.
    const recentlyCompleted = useMemo(() => {
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        return tasks
            .filter((t) => t.completed && !t.archived && t.type !== "recurring")
            .filter((t) => (t.completed_at || 0) >= cutoff)
            .filter((t) => matches(t.text))
            .sort((a, b) => (b.completed_at || 0) - (a.completed_at || 0));
    }, [tasks, s]);
    const eventsTasks = useMemo(
        () =>
            activeTasks
                // Events = every UNIQUE (non-recurring) dated task, timed or not — the same set the
                // Weekly strip counts with the calendar icon, so both views agree on the numbers.
                .filter((t) => t.date && t.type !== "recurring" && !t.completed)
                .filter((t) => matches(t.text))
                .sort((a, b) => {
                    const dd = daysDiff(a.date) - daysDiff(b.date);
                    if (dd !== 0) return dd;
                    return (a.time || "").localeCompare(b.time || "");
                }),
        [activeTasks, s],
    );
    const recurringTasks = useMemo(
        () => {
            // Show ALL recurring tasks regardless of completion — recurring items should never
            // permanently disappear from this list. The card's strikethrough will only apply
            // when the task was completed within its current interval (see CompactTaskCard).
            return activeTasks
                .filter((t) => t.type === "recurring")
                .filter((t) => matches(t.text))
                .sort((a, b) => (a.date ? daysDiff(a.date) : 0) - (b.date ? daysDiff(b.date) : 0));
        },
        [activeTasks, s],
    );

    const archivedTasks = useMemo(
        () => tasks.filter((t) => t.archived).filter((t) => matches(t.text)),
        [tasks, s],
    );

    // ---- section header ----
    const SectionHeader = ({ title, sub: subtitle, right, date }) => (
        <div className="flex items-end justify-between mt-4 mb-3">
            <div>
                <h3 className="font-brand text-xl font-extrabold uppercase tracking-tight text-zinc-100 flex items-baseline gap-2 flex-wrap">
                    <span>{title}</span>
                    {date && (
                        <span className="font-mono font-medium normal-case tracking-normal text-xs sm:text-sm text-zinc-500" data-testid={`section-date-${String(title).toLowerCase()}`}>
                            <span className="text-zinc-600 mr-1.5">·</span>{formatDate(date)}
                        </span>
                    )}
                </h3>
                {subtitle && (
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono flex items-center gap-1 mt-0.5">
                        <span className="text-meye-fuchsia">⚡</span> {subtitle}
                    </p>
                )}
            </div>
            {right}
        </div>
    );

    // ---- render sub-view ----
    const renderContent = () => {
        const tomorrowIso = (() => { const d = new Date(); d.setDate(d.getDate() + 1); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; })();
        const commonTaskProps = {
            onToggle: toggleTask,
            onEdit: setEditingTask,
            attachments,
            searchTerm: search,
            onPostpone: postponeTask,
            onUndoPostpone: undoPostpone,
        };

        if (primary === "time" && sub === "daily") {
            return (
                <>
                    {overdueTasks.length > 0 && (
                        <>
                            <SectionHeader title="Overdue" sub={`${overdueTasks.length} past due`} />
                            <div className="space-y-1.5" data-testid="overdue-list">
                                {overdueTasks.map((t) => {
                                    // Only show the snooze-later chip when it actually makes sense
                                    // (recurring tasks whose cycle repeats, OR malformed recurring
                                    // rows with no interval — same visual family). One-shot
                                    // scheduled tasks in Overdue don't need it: swipe-complete or
                                    // edit-to-reschedule is the right move for them.
                                    const showChip = t.type === "recurring";
                                    const chip = snoozeChipFor();
                                    return (
                                    <CompactTaskCard
                                        key={t.id}
                                        task={t}
                                        {...commonTaskProps}
                                        // Overdue recurring row: exactly ONE missed beat → the checkbox completes
                                        // that beat's source key directly. 2+ missed → open the Overdue Dose
                                        // Picker so the user chooses which occurrence is being completed. Never
                                        // today's date, never an implicit "latest missed" default.
                                        occurrenceIso={t.type === "recurring" && t._missedOccurrences?.length === 1
                                            ? t._missedOccurrences[0]
                                            : undefined}
                                        onToggle={(task, iso) => {
                                            if (task.type === "recurring" && (task._missedOccurrences?.length || 0) > 1) { setDosePickerTask(task); return; }
                                            return toggleTask(task, iso, "overdue");
                                        }}
                                        onEdit={openOverdueEdit}
                                        onOpenMissed={setMissedForTask}
                                        rightSlot={showChip ? (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); snoozeUntilTonight(t); }}
                                                className="text-[9px] font-brand font-bold uppercase tracking-widest px-1.5 py-0.5 rounded border border-amber-400/50 bg-amber-500/10 text-amber-200 hover:bg-amber-500/25 flex-shrink-0"
                                                title={chip.title}
                                                data-testid={`missed-snooze-tonight-${t.id}`}
                                            >
                                                {chip.label}
                                            </button>
                                        ) : null}
                                    />
                                    );
                                })}
                            </div>
                        </>
                    )}
                    <SectionHeader title="Today" date={localDay} sub={todayTasks.length ? `${todayTasks.length} due today` : "nothing scheduled"} />
                    {todayTasks.length === 0 && overdueTasks.length === 0 ? (
                        <EmptyState label="ask the eye to add tasks" />
                    ) : (
                        <div className="space-y-1.5" data-testid="today-list">
                            {todayTasks.map((t) => (
                                <CompactTaskCard
                                    key={`${t.id}:${t._occKey || ""}`}
                                    task={t}
                                    sectionIso={todayStr()}
                                    occurrenceIso={t._occKey || todayStr()}
                                    {...commonTaskProps}
                                    onToggle={(task, iso) => toggleTask(task, iso, "today")}
                                    undoFromIso={t._movedFrom || null}
                                />
                            ))}
                        </div>
                    )}
                    {inboxTasks.length > 0 && (
                        <>
                            <SectionHeader title="Inbox" sub={`${inboxTasks.length} to sort · from Quick Capture`} />
                            <div className="space-y-1.5" data-testid="inbox-list">
                                {inboxTasks.map((t) => (
                                    <CompactTaskCard key={t.id} task={t} {...commonTaskProps} />
                                ))}
                            </div>
                        </>
                    )}
                    {tomorrowTasks.length > 0 && (
                        <>
                            <SectionHeader title="Tomorrow" date={tomorrowIso} sub={`${tomorrowTasks.length} scheduled`} />
                            <div className="space-y-1.5" data-testid="tomorrow-list">
                                {tomorrowTasks.map((t) => (
                                    <CompactTaskCard key={`${t.id}:${t._occKey || ""}`} task={t} sectionIso={tomorrowIso} occurrenceIso={t._occKey || tomorrowIso} undoFromIso={t._movedFrom || null} onUndoPostpone={undoPostpone} {...commonTaskProps} onToggle={(task, iso) => toggleTask(task, iso, "tomorrow")} />
                                ))}
                            </div>
                        </>
                    )}
                    {somedayTasks.length > 0 && (
                        <>
                            <SectionHeader title="Someday" sub="no date · sorted by priority" />
                            <div className="space-y-1.5" data-testid="someday-list">
                                {somedayTasks.map((t) => (
                                    <CompactTaskCard key={t.id} task={t} {...commonTaskProps} />
                                ))}
                            </div>
                        </>
                    )}
                    {recentlyCompleted.length > 0 && (
                        <>
                            <details className="mt-6 group" data-testid="recently-completed-details">
                                <summary className="cursor-pointer list-none flex items-baseline justify-between select-none">
                                    <div>
                                        <h3 className="font-brand text-xl font-extrabold uppercase tracking-tight text-zinc-500 group-open:text-zinc-300 transition-colors">
                                            Just Completed
                                        </h3>
                                        <p className="text-[10px] uppercase tracking-widest text-zinc-600 font-mono flex items-center gap-1 mt-0.5">
                                            <span className="text-emerald-400">✓</span>
                                            {recentlyCompleted.length} in the last 7 days · tap to un-complete
                                        </p>
                                    </div>
                                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono group-open:hidden">show</span>
                                    <span className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono hidden group-open:inline">hide</span>
                                </summary>
                                <div className="space-y-1.5 mt-3 opacity-80" data-testid="recently-completed-list">
                                    {recentlyCompleted.map((t) => (
                                        <CompactTaskCard key={t.id} task={t} {...commonTaskProps} />
                                    ))}
                                </div>
                            </details>
                        </>
                    )}
                </>
            );
        }
        if (primary === "time" && sub === "weekly") {
            return (
                <>
                    <SectionHeader title="Weekly" sub="showing: unique events only" />
                    <WeeklyView tasks={activeTasks} attachments={attachments} onToggle={(t, iso) => toggleTask(t, iso, "weekly")} onEdit={setEditingTask} searchTerm={search} onPostpone={postponeTask} onUndoPostpone={undoPostpone} />
                </>
            );
        }
        if (primary === "time" && sub === "monthly") {
            return (
                <>
                    <SectionHeader title="Monthly" sub="showing: unique events only" />
                    <MonthlyView tasks={activeTasks} attachments={attachments} onToggle={(t, iso) => toggleTask(t, iso, "monthly")} onEdit={setEditingTask} searchTerm={search} onPostpone={postponeTask} onUndoPostpone={undoPostpone} />
                </>
            );
        }
        if (primary === "events" && sub === "events") {
            // Events view — full monthly calendar rendering ONLY dated non-recurring tasks with
            // a time (`eventsTasks`). Recurring tasks are intentionally filtered out here so the
            // calendar reads clean and easy to scan — that's the whole point of Events per the
            // user's request. Click a day to see that day's events below the grid.
            return (
                <>
                    <SectionHeader title="Events" sub="calendar view · one-shot events only" />
                    {eventsTasks.length === 0 ? (
                        <EmptyState label="no scheduled events" />
                    ) : (
                        <MonthlyView
                            tasks={eventsTasks}
                            attachments={attachments}
                            onToggle={(t, iso) => toggleTask(t, iso, "events-monthly")}
                            onEdit={setEditingTask}
                            searchTerm={search}
                            onPostpone={postponeTask}
                            onUndoPostpone={undoPostpone}
                        />
                    )}
                </>
            );
        }
        if (primary === "events" && sub === "recurring") {
            return <RecurringView tasks={tasks} onToggle={(t, iso) => toggleTask(t, iso, "recurring-view")} onPickMissed={(t) => setDosePickerTask(t)} onEdit={setEditingTask} searchTerm={search} onTasksRepaired={async () => setTasks(await api.listTasks())} />;
        }
        if (primary === "docs" && sub === "ledger") {
            // Ledger tab: dynamic ledger note lives inside the regular `notes` collection with
            // a `ledger` tag OR a title that STARTS with "ledger" (optionally after an emoji or
            // whitespace). Substring anywhere is too loose — the tutorial note "log 'ledger'…"
            // would leak in. Reuse NoteCard so all the usual edit / attach / linkify wiring
            // keeps working — this tab is a hoisted view, not a new data type.
            const isLedger = (n) => (n.tags || []).map((t) => t.toLowerCase()).includes("ledger")
                || /^[\s\p{Emoji_Presentation}\p{Extended_Pictographic}]*ledger\b/iu.test(n.text || "");
            const ledgerNotes = notes.filter(isLedger).filter((n) => matches(n.text));
            return (
                <>
                    <SectionHeader title="Ledger" sub="dynamic running notes" />
                    {ledgerNotes.length === 0 ? (
                        <EmptyState label="tag a note with #ledger to promote it here" />
                    ) : (
                        <div className="grid grid-cols-1 gap-2" data-testid="ledger-list">
                            {ledgerNotes.map((n) => (
                                <NoteCard
                                    key={n.id}
                                    note={n}
                                    attachments={attachments}
                                    onUpdate={updateNote}
                                    onDelete={deleteNote}
                                    refreshAttachments={refreshAttachments}
                                    onImagePreview={setPreviewAttachment}
                                    searchTerm={search}
                                />
                            ))}
                        </div>
                    )}
                </>
            );
        }
        if (primary === "docs" && sub === "notes") {
            // Notes tagged/titled with `ledger` are hoisted to their own tab — keep them out of
            // the generic Notes list so they aren't duplicated across two views. Same title-
            // starts-with rule as the Ledger tab.
            const isLedger = (n) => (n.tags || []).map((t) => t.toLowerCase()).includes("ledger")
                || /^[\s\p{Emoji_Presentation}\p{Extended_Pictographic}]*ledger\b/iu.test(n.text || "");
            const generalNotes = notes.filter((n) => !isLedger(n));
            return (
                <>
                    <SectionHeader title="Notes" />
                    {generalNotes.filter((n) => matches(n.text) || matches(n.title)).length === 0 ? (
                        <EmptyState label="capture something" />
                    ) : (
                        <div className="grid grid-cols-1 gap-2">
                            {generalNotes
                                .filter((n) => matches(n.text) || matches(n.title))
                                .map((n) => (
                                    <NoteCard
                                        key={n.id}
                                        note={n}
                                        attachments={attachments}
                                        onUpdate={updateNote}
                                        onDelete={deleteNote}
                                        refreshAttachments={refreshAttachments}
                                        onImagePreview={setPreviewAttachment}
                                        searchTerm={search}
                                    />
                                ))}
                        </div>
                    )}
                </>
            );
        }
        if (primary === "docs" && sub === "scratchpad") {
            return (
                <>
                    <SectionHeader title="Scratchpad Notes" sub="excluded from AI & global search" />
                    {scratchpads.filter((sp) => matches(sp.text)).length === 0 ? (
                        <EmptyState label="brain-dump anything — the AI can't see this" />
                    ) : (
                        <div className="space-y-2">
                            {scratchpads
                                .filter((sp) => matches(sp.text))
                                .map((sp) => (
                                    <div
                                        key={sp.id}
                                        className="bg-meye-card border border-meye-border rounded-md p-3 fade-up flex items-start justify-between gap-2"
                                        data-testid={`scratchpad-${sp.id}`}
                                    >
                                        <p className="text-sm text-zinc-200 whitespace-pre-wrap break-words flex-1">{linkify(sp.text)}</p>
                                        <button
                                            onClick={() => deleteScratchpad(sp.id)}
                                            className="text-zinc-500 hover:text-red-400 flex-shrink-0"
                                            data-testid={`scratchpad-delete-${sp.id}`}
                                        >
                                            <Trash2 size={13} />
                                        </button>
                                    </div>
                                ))}
                        </div>
                    )}
                </>
            );
        }
        if (primary === "docs" && sub === "inventory") {
            const filtered = inventory.filter(
                (i) => matches(i.name) || matches(i.bin) || matches(i.notes) || matches(i.category),
            );
            return (
                <>
                    <SectionHeader title="Equipment & Logs" sub="tag it · bin it · find it later" />
                    <InventoryAddForm onCreate={createInventory} />
                    <div className="mt-3 space-y-1.5">
                        {filtered.length === 0 ? (
                            <EmptyState label="log your first item above" />
                        ) : (
                            filtered.map((i) => (
                                <InventoryCard
                                    key={i.id}
                                    item={i}
                                    onUpdate={updateInventory}
                                    onDelete={deleteInventory}
                                />
                            ))
                        )}
                    </div>
                </>
            );
        }
        if (primary === "docs" && sub === "bookmarks") {
            // Bookmarks come in from the Chrome extension AND from the in-app URL bar. Filter by
            // any visible text so the global search bar still narrows results.
            const filtered = bookmarks.filter(
                (b) => matches(b.title) || matches(b.summary) || matches(b.site) || matches(b.category) || (b.tags || []).some(matches),
            );
            const onCreated = (bm) => setBookmarks((prev) => {
                // If the same URL was reused, replace the existing entry; else prepend.
                const idx = prev.findIndex((x) => x.id === bm.id);
                if (idx !== -1) {
                    const next = [...prev];
                    next[idx] = bm;
                    return next;
                }
                return [bm, ...prev];
            });
            const onPromoted = (bm) => {
                setBookmarks((prev) => prev.map((x) => (x.id === bm.id ? bm : x)));
                // Refresh glossary so the newly-promoted term shows up in the Glossary tab
                api.listGlossary().then(setGlossary).catch(() => {});
            };
            const onDelete = async (id) => {
                try {
                    await api.deleteBookmark(id);
                    setBookmarks((prev) => prev.filter((x) => x.id !== id));
                    toast.success("bookmark deleted");
                } catch {
                    toast.error("delete failed");
                }
            };
            return (
                <>
                    <SectionHeader title="Bookmarks" sub="paste a URL · or capture from the Chrome extension" />
                    <BookmarkAddForm onCreated={onCreated} />
                    <div className="mt-3 space-y-1.5" data-testid="bookmarks-list">
                        {filtered.length === 0 ? (
                            <EmptyState label="paste your first link above" />
                        ) : (
                            filtered.map((b) => (
                                <BookmarkCard
                                    key={b.id}
                                    bookmark={b}
                                    onDelete={onDelete}
                                    onPromoted={onPromoted}
                                />
                            ))
                        )}
                    </div>
                </>
            );
        }
        if (primary === "tools" && sub === "checklists") {
            return (
                <>
                    <SectionHeader title="Checklists" />
                    {checklists.filter((c) => matches(c.title) || (c.items || []).some((it) => matches(it.text))).length === 0 ? (
                        <EmptyState label="craft a routine" />
                    ) : (
                        <div className="grid grid-cols-1 gap-2">
                            {checklists
                                .filter((c) => matches(c.title) || (c.items || []).some((it) => matches(it.text)))
                                .map((c) => (
                                    <ChecklistCard
                                        key={c.id}
                                        checklist={c}
                                        onUpdate={updateChecklist}
                                        onDelete={deleteChecklist}
                                        onConvertedToInventory={() => loadAll()}
                                    />
                                ))}
                        </div>
                    )}
                </>
            );
        }
        if (primary === "tools" && sub === "glossary") {
            const uncategorised = glossary.filter((g) => !g.category || g.category === "other").length;
            const runAutoCat = async (force = false) => {
                try {
                    const res = await api.autoCategorizeGlossary(force);
                    toast.success(res.message || `queued ${res.queued} terms — refresh in 30s`);
                    let attempts = 0;
                    const poll = setInterval(async () => {
                        attempts++;
                        await loadAll();
                        if (attempts >= 9) clearInterval(poll);
                    }, 10000);
                } catch (e) {
                    toast.error("auto-classify failed");
                }
            };
            const runSuggest = async () => {
                const tid = toast.loading("looking for strong links...");
                try {
                    const res = await api.suggestGlossaryLinks();
                    setLinkSuggestions(res.suggestions || []);
                    toast.success(`${res.suggestions?.length || 0} suggestion(s) found`, { id: tid });
                } catch {
                    toast.error("suggest failed", { id: tid });
                }
            };
            const acceptSuggestion = async (s) => {
                const term = glossary.find((g) => g.id === s.term_id);
                if (!term) return;
                try {
                    if (s.type === "checklist") {
                        const next = Array.from(new Set([...(term.linked_checklist_ids || []), s.target_id]));
                        const upd = await api.updateGlossary(term.id, { linked_checklist_ids: next });
                        setGlossary((prev) => prev.map((x) => (x.id === upd.id ? upd : x)));
                    } else if (s.type === "task") {
                        const next = Array.from(new Set([...(term.linked_task_ids || []), s.target_id]));
                        const upd = await api.updateGlossary(term.id, { linked_task_ids: next });
                        setGlossary((prev) => prev.map((x) => (x.id === upd.id ? upd : x)));
                    }
                    setLinkSuggestions((prev) => prev.filter((x) => x !== s));
                } catch {
                    toast.error("link failed");
                }
            };
            const dismissSuggestion = (s) => setLinkSuggestions((prev) => prev.filter((x) => x !== s));

            return (
                <>
                    <SectionHeader title="Glossary & Aliases" sub="the eye is learning" />
                    <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-meye-cyan/30 bg-meye-cyan/[0.04] p-3">
                        <div>
                            <p className="text-[10px] uppercase tracking-widest text-meye-cyan font-mono">smart tools</p>
                            <p className="text-xs text-zinc-400 mt-0.5">
                                {uncategorised > 0
                                    ? `${uncategorised} terms need a category · classify + suggest smart links`
                                    : "All categorised. Suggest hard-links from tasks + checklists."}
                            </p>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {uncategorised > 0 && (
                                <button
                                    onClick={() => runAutoCat(false)}
                                    className="text-[11px] font-brand font-bold uppercase tracking-widest px-3 py-1.5 rounded bg-meye-cyan text-black hover:bg-cyan-300 transition-colors"
                                    data-testid="glossary-autocat-btn"
                                >
                                    classify
                                </button>
                            )}
                            <button
                                onClick={runSuggest}
                                className="text-[11px] font-brand font-bold uppercase tracking-widest px-3 py-1.5 rounded bg-meye-fuchsia text-black hover:bg-fuchsia-400 transition-colors"
                                data-testid="glossary-suggest-btn"
                            >
                                suggest links
                            </button>
                            <button
                                onClick={() => runAutoCat(true)}
                                className="text-[11px] font-brand font-bold uppercase tracking-widest px-3 py-1.5 rounded border border-meye-border bg-black/40 text-zinc-300 hover:border-meye-cyan hover:text-meye-cyan transition-colors"
                                data-testid="glossary-autocat-force-btn"
                            >
                                re-classify all
                            </button>
                        </div>
                    </div>

                    {/* Suggested links tray */}
                    {linkSuggestions.length > 0 && (
                        <div className="mb-3 rounded-md border border-meye-fuchsia/30 bg-meye-fuchsia/[0.05] p-3 space-y-2" data-testid="glossary-suggestions">
                            <p className="text-[10px] uppercase tracking-widest text-meye-fuchsia font-mono">
                                proposed links · {linkSuggestions.length}
                            </p>
                            {linkSuggestions.map((s, i) => (
                                <div
                                    key={i}
                                    className="flex items-start gap-2 rounded border border-meye-border bg-black/30 p-2"
                                    data-testid={`glossary-suggestion-${i}`}
                                >
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs">
                                            <span className="font-brand font-bold text-meye-cyan uppercase tracking-widest">{s.term}</span>
                                            <span className="text-zinc-500 mx-1.5">→</span>
                                            <span className={`text-[10px] font-mono uppercase tracking-widest ${s.type === "task" ? "text-meye-fuchsia" : "text-meye-cyan"}`}>
                                                {s.type}
                                            </span>
                                            <span className="text-zinc-200 ml-1.5">{s.target_label}</span>
                                        </p>
                                        {s.reason && (
                                            <p className="text-[10px] text-zinc-500 font-mono mt-0.5 italic">{s.reason}</p>
                                        )}
                                    </div>
                                    <div className="flex gap-1 flex-shrink-0">
                                        <button
                                            onClick={() => acceptSuggestion(s)}
                                            className="text-[10px] font-brand font-bold uppercase tracking-widest px-2 py-1 rounded bg-emerald-500/80 text-black hover:bg-emerald-400"
                                            data-testid={`glossary-suggestion-accept-${i}`}
                                        >
                                            link
                                        </button>
                                        <button
                                            onClick={() => dismissSuggestion(s)}
                                            className="text-[10px] font-mono uppercase tracking-widest px-2 py-1 rounded border border-meye-border bg-black/40 text-zinc-500 hover:text-white"
                                        >
                                            skip
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}

                    {glossary.filter((g) => matches(g.term) || matches(g.notes)).length === 0 ? (
                        <EmptyState label='teach the eye: try "Prospera is my bank"' />
                    ) : (
                        <div className="space-y-1.5">
                            {glossary
                                .filter((g) => matches(g.term) || matches(g.notes))
                                .map((g) => (
                                    <GlossaryCard
                                        key={g.id}
                                        term={g}
                                        checklists={checklists}
                                        tasks={tasks}
                                        onDelete={deleteGlossary}
                                        onUpdated={(upd) =>
                                            setGlossary((prev) => prev.map((x) => (x.id === upd.id ? upd : x)))
                                        }
                                    />
                                ))}
                        </div>
                    )}
                </>
            );
        }
        if (primary === "tools" && sub === "archive") {
            return (
                <>
                    <SectionHeader title="Archive" sub="completed & archived" />
                    {archivedTasks.length === 0 ? (
                        <EmptyState label="the vault is empty" />
                    ) : (
                        <div className="space-y-1.5">
                            {archivedTasks.map((t) => (
                                <CompactTaskCard key={t.id} task={t} attachments={attachments} onToggle={(task, iso) => toggleTask(task, iso, "archive")} onEdit={setEditingTask} searchTerm={search} />
                            ))}
                        </div>
                    )}
                </>
            );
        }
        if (primary === "tools" && sub === "backup") {
            return <BackupPanel onDataChanged={loadAll} />;
        }
        if (primary === "tools" && sub === "diagnostics") {
            return (
                <DiagnosticsPanel
                    tasks={tasks}
                    user={authUser}
                    view={{ primary, sub, screensaver: screensaverOn }}
                    counts={{ today: todayTasks.length, tomorrow: tomorrowTasks.length, overdue: overdueTasks.length }}
                    visible={[...overdueTasks, ...todayTasks, ...tomorrowTasks]}
                    openTask={editingTask}
                />
            );
        }
        if (primary === "tools" && sub === "cleanup") {
            return <DataHygienePanel refreshKey={tasks.map((t) => `${t.id}:${t.text}:${t.description || ""}`).join("|")} onDataChanged={loadAll} onEdit={(id) => { const t = tasks.find((x) => x.id === id); if (t) setEditingTask(t); }} />;
        }
        if (primary === "comms") {
            return (
                <MissionChat
                    settings={settings}
                    tasks={tasks}
                    openHint={commsHint}
                    autoVoice={commsAutoVoice}
                    onHintConsumed={() => {
                        setCommsHint(null);
                        setCommsAutoVoice(false);
                    }}
                    onToggleTask={toggleTask}
                    onEditTask={setEditingTask}
                    onDataChanged={loadAll}
                    onSettingsChange={async (patch) => {
                        const upd = await api.updateSettings(patch);
                        setSettings(upd);
                    }}
                />
            );
        }
        if (primary === "support") {
            return (
                <>
                    <SectionHeader title="Support" sub="commands · backup · about" />
                    <div className="grid grid-cols-1 gap-2 mt-2">
                        <div className="rounded-lg border border-meye-border bg-meye-card/60 p-4">
                            <p className="text-[11px] uppercase tracking-widest text-meye-cyan font-mono mb-2">chat commands</p>
                            <div className="space-y-1 text-xs text-zinc-300 font-mono">
                                <div><span className="text-meye-fuchsia">import</span> — upload .json backup</div>
                                <div><span className="text-meye-fuchsia">export</span> — download .json backup</div>
                                <div><span className="text-meye-fuchsia">note: ...</span> — quick save note</div>
                                <div><span className="text-meye-fuchsia">I'm at [place]</span> — context scan</div>
                                <div><span className="text-meye-fuchsia">help</span> or <span className="text-meye-fuchsia">?</span> — show commands</div>
                            </div>
                        </div>
                        <div className="rounded-lg border border-meye-border bg-meye-card/60 p-4">
                            <p className="text-[11px] uppercase tracking-widest text-meye-cyan font-mono mb-2">gestures</p>
                            <div className="space-y-1 text-xs text-zinc-300 font-mono">
                                <div>swipe task right → complete</div>
                                <div>swipe task left → edit</div>
                                <div>double-tap screensaver → enter</div>
                                <div>60s idle → screensaver</div>
                            </div>
                        </div>
                        <div className="rounded-lg border border-meye-border bg-meye-card/60 p-4">
                            <p className="text-[11px] uppercase tracking-widest text-meye-cyan font-mono mb-2">about</p>
                            <p className="text-xs text-zinc-400">
                                mEYEtasks · powered by mEYEtrainer AI (Claude Fable 5). your personal knowledge graph, watched by the eye.
                            </p>
                        </div>
                    </div>
                </>
            );
        }
        return null;
    };

    // ---- top bar ----
    return (
        <div className="App min-h-screen text-zinc-100 relative">
            {/* Onboarding — must resolve before anything else is usable */}
            {settings && !isRealName(settings.user_name) && (
                <OnboardingDialog
                    onSubmit={async ({ userName }) => {
                        const patch = { user_name: userName };
                        // On first run, name the assistant "mEYEtrainer" so the user isn't
                        // asked to pick a name/avatar. They can still customise later via
                        // Tools > Settings.
                        if (!settings.assistant_name) patch.assistant_name = "mEYEtrainer";
                        const upd = await api.updateSettings(patch);
                        setSettings(upd);
                        const displayUser = (userName || upd.user_name || "trainer").toLowerCase();
                        setChatLog((prev) => [
                            ...prev,
                            {
                                role: "assistant",
                                text: `mEYEtrainer online. nice to meet you, ${displayUser}.`,
                                ts: Date.now(),
                            },
                        ]);
                        toast.success(`welcome, ${userName || upd.user_name}`);
                        setScreensaverOn(false);
                    }}
                />
            )}
            {isRealName(settings?.user_name) && screensaverOn && !isDesktop && (
                <Screensaver
                    tasks={tasks}
                    userName={settings.user_name}
                    assistantName={settings.assistant_name}
                    assistantAvatarId={settings.assistant_avatar}
                    contextScan={contextScan}
                    onExit={(task) => {
                        setScreensaverOn(false);
                        if (!task) return;
                        if (task._noOccurrence) { toast.error("No outstanding occurrence to edit — list refreshed"); loadAll(); return; }
                        // 2+ outstanding beats: never guess which one — pick first, then edit that exact source key.
                        if (task._needsOccurrencePick) { setDosePickerTask({ ...task, _pickMode: "edit" }); return; }
                        setEditingTask(task);
                    }}
                    onOpenComms={(window) => {
                        setScreensaverOn(false);
                        if (window) {
                            setCommsHint(
                                `(user just tapped COMMS from the "${window.label}" screensaver window — greet them by name in ONE short spoken sentence (under 12 words). Do not create tasks. Then wait.)`,
                            );
                        }
                        setCommsAutoVoice(true);
                        setPrimary("comms");
                    }}
                    onOpenSection={(sectionId) => {
                        setScreensaverOn(false);
                        setPrimary(sectionId);
                        if (sectionId === "comms") {
                            setCommsAutoVoice(true);
                        }
                    }}
                    onSearch={(query) => {
                        // Feeds the app's normal global search — same grouped inline results as
                        // when typing in the desktop search bar. Landing on "time" (Daily) shows
                        // matched tasks/events; other sections filter their own lists via searchTerm.
                        setSearch(query);
                        setPrimary("time");
                        setScreensaverOn(false);
                    }}
                    onQuickCaptured={() => { loadAll(); }}
                />
            )}
            <Toaster
                theme="dark"
                position="top-center"
                toastOptions={{
                    style: {
                        background: "#121212",
                        border: "1px solid #27272a",
                        color: "#f3f4f6",
                        fontFamily: "JetBrains Mono, monospace",
                        fontSize: "12px",
                    },
                }}
            />

            {/* Header */}
            <header className="sticky top-0 z-30 backdrop-blur-xl bg-black/70 border-b border-meye-border/60">
                <div className={`${isDesktop ? "max-w-[1600px]" : "max-w-lg md:max-w-3xl lg:max-w-4xl"} mx-auto px-4 lg:px-8 pt-4 pb-2`}>
                    <div className="flex items-center gap-3">
                        <ApertureEye size={26} />
                        <h1 className="font-brand text-3xl font-black tracking-tight text-zinc-100 leading-none flex-1">
                            m<span className="text-meye-fuchsia text-glow-fuchsia">EYE</span>tasks
                        </h1>
                        <button
                            title={dndActive ? "Do Not Disturb — click to unmute" : "Silence nags for 2 hours"}
                            onClick={toggleDnd}
                            className={`transition-colors ${dndActive ? "text-red-400 hover:text-red-300" : "text-zinc-500 hover:text-meye-cyan"}`}
                            data-testid="dnd-toggle-btn"
                        >
                            {dndActive ? <ShieldOff size={18} strokeWidth={1.5} /> : <ShieldCheck size={18} strokeWidth={1.5} />}
                        </button>
                        <button
                            title="Notifications"
                            onClick={() => setNotifOpen((v) => !v)}
                            className={`relative transition-colors ${notifOpen ? "text-meye-cyan" : "text-zinc-500 hover:text-meye-cyan"}`}
                            data-testid="notifications-btn"
                        >
                            <Bell size={18} strokeWidth={1.5} />
                            {(() => {
                                const today = todayStr();
                                const count = tasks.filter(
                                    (t) => !t.completed && !t.archived && t.date && t.date <= today,
                                ).length;
                                return count > 0 ? (
                                    <span
                                        className="absolute -top-1 -right-1 min-w-[14px] h-[14px] rounded-full bg-meye-fuchsia text-black text-[9px] font-bold flex items-center justify-center px-0.5"
                                        data-testid="notifications-badge"
                                    >
                                        {count > 9 ? "9+" : count}
                                    </span>
                                ) : null;
                            })()}
                        </button>
                        <button
                            title="Settings"
                            onClick={() => setSettingsOpen(true)}
                            className="text-zinc-500 hover:text-meye-cyan"
                            data-testid="settings-open-btn"
                            aria-label="Open settings"
                        >
                            <SettingsIcon size={18} strokeWidth={1.5} />
                        </button>
                        <button
                            onClick={() => setTrainerOn((v) => !v)}
                            className={`px-3 py-1.5 rounded-full text-[10px] font-brand font-bold uppercase tracking-widest border flex items-center gap-1.5 transition-colors ${
                                trainerOn
                                    ? "border-orange-500/60 bg-gradient-to-r from-orange-600/20 to-amber-600/20 text-orange-400"
                                    : "border-zinc-700 bg-zinc-800/40 text-zinc-500"
                            }`}
                            data-testid="trainer-toggle-btn"
                        >
                            <Brain size={12} strokeWidth={2} />
                            Turn Trainer {trainerOn ? "Off" : "On"}
                        </button>
                    </div>

                    <div className="mt-4">
                        <MainNav primary={primary} onChange={setPrimary} hideComms={isDesktop} />
                        <SubNav tabs={SUB_TABS[primary]} active={sub} onChange={setSub} />
                    </div>

                    {/* search — highlighted pill matching the DAILY/WEEKLY/MONTHLY toggle bar
                        above it. Cyan accent border + glow so it reads as a top-level control
                        instead of a whisper-thin utility input. The manual "+ task" pill sits
                        to the right — it POSTs directly to /api/tasks, bypassing the AI so the
                        assistant can never re-interpret a plain task dictation as a request
                        for content. */}
                    <div className="mt-3 flex items-stretch gap-2">
                        <div className="relative flex-1 group">
                            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-meye-cyan/25 via-transparent to-meye-fuchsia/25 blur-md opacity-60 group-focus-within:opacity-100 transition-opacity pointer-events-none" aria-hidden="true" />
                            <div className="relative">
                                <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-meye-cyan" strokeWidth={2} />
                                <input
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    placeholder="search everything..."
                                    className="w-full bg-meye-card/70 backdrop-blur-sm border border-meye-cyan/50 rounded-full pl-11 pr-11 py-3 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:border-meye-cyan focus:shadow-[0_0_18px_rgba(34,211,238,0.35)] transition-shadow font-mono"
                                    data-testid="global-search"
                                />
                                {search && (
                                    <button
                                        type="button"
                                        onClick={() => setSearch("")}
                                        className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center text-zinc-500 hover:text-zinc-100 hover:bg-zinc-800/60 transition-colors"
                                        data-testid="search-clear"
                                        aria-label="Clear search"
                                    >
                                        <X size={14} strokeWidth={2.5} />
                                    </button>
                                )}
                            </div>
                        </div>
                        <ManualTaskButton onCreated={upsertTaskLocal} />
                    </div>
                    {search.trim().length >= 2 && (() => {
                        // Tiny "no matches" hint stays as sibling of the search input;
                        // the grouped results themselves render in the main content area below.
                        const q = search.trim();
                        const score = (...fields) => searchScore(q, ...fields);
                        const total =
                            activeTasks.filter((t) => score(t.text) > 0).length +
                            notes.filter((n) => score(n.text) > 0).length +
                            inventory.filter((i) => score(i.name, i.bin, i.notes, i.category) > 0).length +
                            checklists.filter((c) => score(c.title, ...(c.items || []).map((it) => it.text)) > 0).length +
                            bookmarks.filter((b) => score(b.title, b.url, b.summary, b.category) > 0).length +
                            scratchpads.filter((sp) => score(sp.text) > 0).length;
                        if (total === 0) {
                            return (
                                <div className="mt-2 text-[10px] font-mono text-zinc-500 px-1" data-testid="search-nohits">
                                    no matches anywhere
                                </div>
                            );
                        }
                        return null;
                    })()}
                </div>
            </header>

            {/* Main */}
            <main className={`${isDesktop ? "max-w-[1600px]" : "max-w-lg md:max-w-3xl lg:max-w-4xl"} mx-auto px-4 lg:px-8 pt-3 pb-40 lg:pb-10 relative z-10 overflow-x-clip min-w-0`}>
                {/* Only render the 2-col grid when we're actually going to use the right rail
                    (desktop mouse/hover context). On iPad Chrome the media query for `lg:`
                    fires but the right rail is gated on `isDesktop`, which fails there because
                    iPad reports pointer=coarse. Keeping the grid on would leave a phantom 420px
                    column empty and squeeze the main content into the left half. */}
                <div className={isDesktop ? "grid grid-cols-[minmax(0,1fr)_420px] gap-8" : ""}>
                    <div className="min-w-0">
                        {/* NEURAL LINK panel (mobile only — desktop has the persistent COMMS rail) */}
                        {primary !== "comms" && !isDesktop && (
                            <NeuralLink chatLog={chatLog} userName={settings?.user_name} />
                        )}

                        {/* Context scan banner */}
                        {contextScan && contextScan.surfaced_task_ids?.length > 0 && (
                            <div
                                className="mt-3 rounded-md border border-meye-cyan/40 bg-meye-cyan/5 p-3 fade-up flex items-start justify-between gap-2"
                                data-testid="context-scan-banner"
                            >
                                <div>
                                    <p className="text-[10px] uppercase tracking-widest text-meye-cyan font-mono">
                                        context scan · {contextScan.city}
                                    </p>
                                    <p className="text-xs text-zinc-300 mt-1">
                                        {contextScan.surfaced_task_ids.length} task{contextScan.surfaced_task_ids.length === 1 ? "" : "s"} tied to{" "}
                                        {contextScan.matched_terms.map((t) => t.term).join(" · ")}
                                    </p>
                                </div>
                                <button
                                    onClick={() => setContextScan(null)}
                                    className="text-zinc-500 hover:text-white text-xs"
                                    data-testid="context-scan-dismiss"
                                >
                                    ✕
                                </button>
                            </div>
                        )}

                        {/* Quick-add row — kept for text-only surfaces (notes, checklists, scratchpad).
                            Task creation is intelligent via COMMS — no dumb quick-task input. */}
                        {["notes", "checklists", "scratchpad"].includes(sub) && (
                            <div className="mt-3 flex gap-2">
                                <input
                                    value={newItemText}
                                    onChange={(e) => setNewItemText(e.target.value)}
                                    onKeyDown={(e) => e.key === "Enter" && addForCurrentView()}
                                    placeholder={
                                        sub === "notes"
                                            ? "new note..."
                                            : sub === "checklists"
                                              ? "new checklist title..."
                                              : "brain dump..."
                                    }
                                    className="flex-1 bg-meye-card border border-meye-border rounded-md px-3 py-2 text-sm text-zinc-100 focus:outline-none focus:border-meye-fuchsia/50 font-mono"
                                    data-testid="quick-add-input"
                                />
                                <button
                                    onClick={addForCurrentView}
                                    disabled={!newItemText.trim()}
                                    className="bg-meye-fuchsia hover:bg-fuchsia-500 disabled:opacity-40 text-black rounded-md p-2 transition-colors"
                                    data-testid="quick-add-btn"
                                >
                                    <Plus size={16} strokeWidth={2} />
                                </button>
                            </div>
                        )}

                        <div className="mt-3">
                            {search.trim().length >= 2
                                ? (() => {
                                      const rawQ = search.trim().toLowerCase();
                                      // Include completed tasks only when the user explicitly asks (e.g. "completed dentist").
                                      // Strip the trigger word from the actual match string so the substring match still finds real task text.
                                      const wantCompleted = /\b(completed|done|finished)\b/.test(rawQ);
                                      const q = rawQ.replace(/\b(completed|done|finished)\b/g, "").replace(/\s+/g, " ").trim();
                                      const rank = (...fields) => searchScore(q, ...fields);
                                      // Filter → attach rank → sort by rank desc → strip rank
                                      const scored = (list, project) => list
                                          .map((row) => ({ row, s: rank(...project(row)) }))
                                          .filter((x) => x.s > 0)
                                          .sort((a, b) => b.s - a.s)
                                          .map((x) => x.row);
                                      // Recurring tasks are marked `completed` on every check-in
                                      // (see toggleTask), but they represent an ongoing cadence —
                                      // never hide them from search on that basis. Only exclude
                                      // NON-recurring completed tasks unless the user asked for them.
                                      const tasksHit = scored(
                                          activeTasks.filter((t) => wantCompleted || !t.completed || t.type === "recurring"),
                                          (t) => [t.text, t.description],
                                      );
                                      const eventsHit = tasksHit.filter((t) => t.date && t.time);
                                      const tasksOnly = tasksHit.filter((t) => !(t.date && t.time));
                                      const notesHit = scored(notes, (n) => [n.title, n.text]);
                                      const invHit = scored(inventory, (i) => [i.name, i.bin, i.notes, i.category]);
                                      const clHit = scored(checklists, (c) => [c.title, ...(c.items || []).map((it) => it.text)]);
                                      const bmHit = scored(bookmarks, (b) => [b.title, b.url, b.summary, b.category]);
                                      const spHit = scored(scratchpads, (sp) => [sp.text]);
                                      const total = tasksHit.length + notesHit.length + invHit.length + clHit.length + bmHit.length + spHit.length;
                                      // Semantic-related tasks (only those NOT already in literal results).
                                      const literalIds = new Set(tasksHit.map((t) => t.id));
                                      const relatedTasks = (semantic.query === search.trim() && semantic.task_ids)
                                          ? (semantic.task_ids || [])
                                              .filter((id) => !literalIds.has(id))
                                              .map((id) => activeTasks.find((t) => t.id === id))
                                              .filter(Boolean)
                                          : [];
                                      if (total === 0 && relatedTasks.length === 0 && !semantic.loading) {
                                          return (
                                          <div className="text-center text-zinc-500 text-xs font-mono py-10" data-testid="search-nohits-main">
                                              no matches for {search}
                                          </div>
                                          );
                                      }
                                      const Section = ({ title, count, testid, children }) => (
                                          <div className="mb-5" data-testid={testid}>
                                              <h3 className="text-[11px] font-brand font-bold uppercase tracking-widest text-meye-cyan mb-2 flex items-center gap-2">
                                                  {title}
                                                  <span className="text-zinc-500 font-mono">· {count}</span>
                                              </h3>
                                              <div className="space-y-1.5">{children}</div>
                                          </div>
                                      );
                                      return (
                                          <div data-testid="search-results">
                                              {eventsHit.length > 0 && (
                                                  <Section title="Events" count={eventsHit.length} testid="search-section-events">
                                                      {eventsHit.map((t) => (
                                                          <CompactTaskCard key={t.id} task={t} attachments={attachments} onToggle={(task, iso) => toggleTask(task, iso, "search")} onEdit={setEditingTask} searchTerm={search} />
                                                      ))}
                                                  </Section>
                                              )}
                                              {tasksOnly.length > 0 && (
                                                  <Section title="Tasks" count={tasksOnly.length} testid="search-section-tasks">
                                                      {tasksOnly.map((t) => (
                                                          <CompactTaskCard key={t.id} task={t} attachments={attachments} onToggle={(task, iso) => toggleTask(task, iso, "search")} onEdit={setEditingTask} searchTerm={search} />
                                                      ))}
                                                  </Section>
                                              )}
                                              {notesHit.length > 0 && (
                                                  <Section title="Notes" count={notesHit.length} testid="search-section-notes">
                                                      {notesHit.map((n) => (
                                                          <NoteCard
                                                              key={n.id}
                                                              note={n}
                                                              attachments={attachments}
                                                              onUpdate={updateNote}
                                                              onDelete={deleteNote}
                                                              refreshAttachments={refreshAttachments}
                                                              onImagePreview={setPreviewAttachment}
                                                              searchTerm={search}
                                                          />
                                                      ))}
                                                  </Section>
                                              )}
                                              {invHit.length > 0 && (
                                                  <Section title="Inventory" count={invHit.length} testid="search-section-inventory">
                                                      {invHit.map((i) => (
                                                          <div
                                                              key={i.id}
                                                              className="rounded-md border border-meye-border bg-meye-card/60 px-3 py-2 text-sm text-zinc-200"
                                                              data-testid={`search-inv-${i.id}`}
                                                          >
                                                              <div className="font-brand font-bold text-zinc-100">
                                                                  {highlight(i.name, search)}
                                                              </div>
                                                              {i.bin && (
                                                                  <div className="text-[11px] font-mono text-zinc-500 mt-0.5">
                                                                      bin · {highlight(i.bin, search)}
                                                                  </div>
                                                              )}
                                                              {i.category && (
                                                                  <div className="text-[10px] uppercase tracking-widest text-meye-fuchsia/80 mt-0.5">
                                                                      {highlight(i.category, search)}
                                                                  </div>
                                                              )}
                                                              {i.notes && (
                                                                  <div className="text-xs text-zinc-400 mt-1 break-words">
                                                                      {highlight(i.notes, search)}
                                                                  </div>
                                                              )}
                                                          </div>
                                                      ))}
                                                  </Section>
                                              )}
                                              {clHit.length > 0 && (
                                                  <Section title="Checklists" count={clHit.length} testid="search-section-checklists">
                                                      {clHit.map((c) => {
                                                          const matchedItems = (c.items || []).filter((it) => rank(it.text) > 0);
                                                          return (
                                                              <div
                                                                  key={c.id}
                                                                  className="rounded-md border border-meye-border bg-meye-card/60 px-3 py-2 text-sm text-zinc-200"
                                                                  data-testid={`search-cl-${c.id}`}
                                                              >
                                                                  <div className="font-brand font-bold text-zinc-100">
                                                                      {highlight(c.title, search)}
                                                                  </div>
                                                                  <div className="text-[10px] font-mono text-zinc-500 mt-0.5">
                                                                      {c.items?.length || 0} items{matchedItems.length > 0 && ` · ${matchedItems.length} match`}
                                                                  </div>
                                                                  {/* Show the matching item lines so the user knows WHY the checklist surfaced */}
                                                                  {matchedItems.slice(0, 3).map((it) => (
                                                                      <div
                                                                          key={it.id}
                                                                          className="text-xs text-zinc-300 mt-1 pl-2 border-l border-meye-cyan/60"
                                                                          data-testid={`search-cl-${c.id}-item-${it.id}`}
                                                                      >
                                                                          {it.completed && <span className="text-emerald-400 mr-1">✓</span>}
                                                                          {highlight(it.text, search)}
                                                                      </div>
                                                                  ))}
                                                              </div>
                                                          );
                                                      })}
                                                  </Section>
                                              )}
                                              {bmHit.length > 0 && (
                                                  <Section title="Bookmarks" count={bmHit.length} testid="search-section-bookmarks">
                                                      {bmHit.map((b) => (
                                                          <a
                                                              key={b.id}
                                                              href={b.url}
                                                              target="_blank"
                                                              rel="noopener noreferrer"
                                                              className="block rounded-md border border-meye-border bg-meye-card/60 px-3 py-2 text-sm text-zinc-200 hover:border-meye-cyan/60 transition-colors"
                                                              data-testid={`search-bm-${b.id}`}
                                                          >
                                                              <div className="font-brand font-bold text-zinc-100">
                                                                  {highlight(b.title || b.url, search)}
                                                              </div>
                                                              <div className="text-[10px] font-mono text-meye-cyan/80 mt-0.5 truncate">
                                                                  {highlight(b.url, search)}
                                                              </div>
                                                              {b.category && (
                                                                  <div className="text-[10px] uppercase tracking-widest text-meye-fuchsia/80 mt-0.5">
                                                                      {highlight(b.category, search)}
                                                                  </div>
                                                              )}
                                                              {b.summary && (
                                                                  <div className="text-xs text-zinc-400 mt-1 break-words">
                                                                      {highlight(b.summary, search)}
                                                                  </div>
                                                              )}
                                                          </a>
                                                      ))}
                                                  </Section>
                                              )}
                                              {spHit.length > 0 && (
                                                  <Section title="Scratchpad" count={spHit.length} testid="search-section-scratchpad">
                                                      {spHit.map((sp) => (
                                                          <button
                                                              key={sp.id}
                                                              type="button"
                                                              onClick={() => { setPrimary("docs"); setSub("scratchpad"); }}
                                                              className="block w-full text-left rounded-md border border-meye-border bg-meye-card/60 hover:border-meye-cyan/60 hover:bg-meye-card transition-colors px-3 py-2 text-sm text-zinc-200 whitespace-pre-wrap break-words"
                                                              data-testid={`search-sp-${sp.id}`}
                                                          >
                                                              {linkifyHighlight(sp.text, search)}
                                                          </button>
                                                      ))}
                                                  </Section>
                                              )}
                                              {/* Related — LLM-derived semantic matches when the literal query is a category/theme */}
                                              {(semantic.loading || relatedTasks.length > 0) && (
                                                  <div className="mb-5" data-testid="search-section-related">
                                                      <h3 className="text-[11px] font-brand font-bold uppercase tracking-widest text-meye-fuchsia mb-2 flex items-center gap-2">
                                                          <span>Related</span>
                                                          {semantic.loading ? (
                                                              <span className="text-zinc-500 font-mono">· thinking…</span>
                                                          ) : (
                                                              <span className="text-zinc-500 font-mono">· {relatedTasks.length}</span>
                                                          )}
                                                      </h3>
                                                      {!semantic.loading && semantic.reasoning && (
                                                          <p className="text-[10px] font-mono text-zinc-500 mb-2 italic">
                                                              {semantic.reasoning}
                                                          </p>
                                                      )}
                                                      <div className="space-y-1.5">
                                                          {relatedTasks.map((t) => (
                                                              <CompactTaskCard
                                                                  key={t.id}
                                                                  task={t}
                                                                  attachments={attachments}
                                                                  onToggle={(task, iso) => toggleTask(task, iso, "chat-related")}
                                                                  onEdit={setEditingTask}
                                                                  searchTerm={search}
                                                              />
                                                          ))}
                                                      </div>
                                                  </div>
                                              )}
                                          </div>
                                      );
                                  })()
                                : renderContent()}
                        </div>
                    </div>

                    {/* Persistent COMMS rail — desktop only */}
                    {isDesktop && (
                        <aside
                            className="hidden lg:block sticky self-start"
                            style={{ top: "230px", height: "calc(100vh - 260px)" }}
                            data-testid="desktop-comms-rail"
                        >
                            <div className="h-full rounded-xl border border-meye-border bg-meye-card/40 backdrop-blur-sm p-4 flex flex-col">
                                <MissionChat
                                    settings={settings}
                                    tasks={tasks}
                                    variant="rail"
                                    openHint={commsHint}
                                    autoVoice={commsAutoVoice}
                                    onHintConsumed={() => {
                                        setCommsHint(null);
                                        setCommsAutoVoice(false);
                                    }}
                                    onToggleTask={toggleTask}
                                    onEditTask={setEditingTask}
                                    onDataChanged={loadAll}
                                    onSettingsChange={async (patch) => {
                                        const upd = await api.updateSettings(patch);
                                        setSettings(upd);
                                    }}
                                />
                            </div>
                        </aside>
                    )}
                </div>
            </main>

            <TaskEditor
                task={editingTask}
                open={!!editingTask}
                onOpenChange={(o) => !o && setEditingTask(null)}
                onSave={saveTask}
                onDelete={deleteTask}
                onPostpone={postponeTask}
                onTaskUpdated={(upd) => {
                    setTasks((prev) => prev.map((x) => (x.id === upd.id ? upd : x)));
                    setEditingTask((cur) => (cur && cur.id === upd.id ? { ...cur, ...upd } : cur));
                }}
                onComplete={async (t, iso) => {
                    await toggleTask(t, iso);
                    setEditingTask(null);
                }}
                attachments={attachments}
                refreshAttachments={refreshAttachments}
            />

            <OverdueDosePicker
                key={dosePickerTask?.id ? `dose-${dosePickerTask.id}` : "dose-none"}
                task={dosePickerTask}
                mode={dosePickerTask?._pickMode || "complete"}
                onClose={() => setDosePickerTask(null)}
                onCompleted={(upd) => setTasks((prev) => prev.map((t) => (t.id === upd.id ? upd : t)))}
                onPick={(task, iso) => {
                    if (task._pickMode !== "edit") return toggleTask(task, iso, "dose-picker");
                    const display = effectiveOverrides(task).find((o) => o.from === iso)?.to || iso;
                    const { _pickMode, _needsOccurrencePick, ...rest } = task;
                    setEditingTask({ ...rest, _occKey: iso, _displayIso: display, _occurrenceDate: display });
                }}
            />

            <MissedOccurrencesDialog
                key={missedForTask?.id || "none"}
                task={missedForTask}
                onClose={() => setMissedForTask(null)}
                onCompletionsChanged={(taskId, nextCompletions, nextCompletionTimes) => {
                    setTasks((prev) => prev.map((x) => (x.id === taskId ? { ...x, completions: nextCompletions, completion_times: nextCompletionTimes || x.completion_times } : x)));
                }}
            />

            <SettingsDrawer
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                settings={settings}
                onChange={async (patch) => {
                    const upd = await api.updateSettings(patch);
                    setSettings(upd);
                }}
            />

            <NagOverlay
                task={nagTask}
                open={!!nagTask}
                onClose={() => setNagTask(null)}
                onComplete={async (iso) => {
                    if (nagTask) {
                        await toggleTask(nagTask, iso);
                        setNagTask(null);
                    }
                }}
                onSnoozed={async () => {
                    await loadAll();
                }}
            />

            <NotificationsPanel
                open={notifOpen}
                onClose={() => setNotifOpen(false)}
                tasks={tasks}
                onEditTask={setEditingTask}
            />

            {/* LET'S CHAT owns its own fixed input — hide the global bar there so there is exactly one. */}
            {!isDesktop && primary !== "comms" && (
                <AIChatBar
                    chatLog={chatLog}
                    setChatLog={setChatLog}
                    onAiResult={onAiResult}
                    onDataChanged={loadAll}
                    onSearchIntent={onSearchIntent}
                    onLocationIntent={onLocationIntent}
                    pendingTraining={settings?.pending_training}
                    onAttach={async (p) => {
                        try {
                            const note = await api.createNote({ text: `[attachment] ${p.name}` });
                            await api.createAttachment({ parent_id: note.id, name: p.name, type: p.type, data: p.data });
                            toast.success("Saved to Notes");
                            await loadAll();
                        } catch (e) {
                            toast.error("Attach failed");
                        }
                    }}
                />
            )}

            <MediaLightbox attachment={previewAttachment} onClose={() => setPreviewAttachment(null)} />

            {/* Global Quick Capture FAB removed — now lives under the focus area inside the screensaver (see TimelineScrubber). */}
        </div>
    );
}

export default function AppRoot() {
    // ?iphone=1 → render iPhone-framed preview. We render the actual App component INSIDE
    // the frame (no iframe) so the outer window's auth context, cookies, session state, and
    // service-worker connection are all shared. Previously this used an <iframe src="?mobile=1">
    // which meant a separate document that Chrome would refuse to authenticate through Google
    // OAuth (Google blocks embedding). The direct-render approach fixes the "keeps showing me
    // the login screen inside the phone" bug.
    const isIphoneFrame = typeof window !== "undefined" && /(?:^|[?&])iphone=1/.test(window.location.search);
    if (isIphoneFrame) {
        return (
            <div className="min-h-screen w-full bg-gradient-to-br from-zinc-900 via-zinc-950 to-black flex flex-col items-center justify-center py-8 gap-4">
                <div className="text-[11px] font-mono uppercase tracking-widest text-zinc-500">
                    mEYEtasks · iphone 15 pro preview
                </div>

                {/* iPhone 15 Pro perspective frame */}
                <div className="relative" data-testid="iphone-frame">
                    {/* Outer titanium ring */}
                    <div
                        className="relative w-[402px] h-[860px] rounded-[60px] p-[3px]"
                        style={{
                            background: "linear-gradient(135deg, #6b7280 0%, #1f2937 25%, #4b5563 50%, #111827 75%, #6b7280 100%)",
                            boxShadow: "0 40px 100px rgba(0,0,0,0.9), 0 0 40px rgba(0,0,0,0.5), inset 0 0 2px rgba(255,255,255,0.1)",
                        }}
                    >
                        {/* Volume buttons (left side) */}
                        <div className="absolute -left-[3px] top-[130px] w-[3px] h-[35px] rounded-l bg-zinc-700" />
                        <div className="absolute -left-[3px] top-[190px] w-[3px] h-[65px] rounded-l bg-zinc-700" />
                        <div className="absolute -left-[3px] top-[275px] w-[3px] h-[65px] rounded-l bg-zinc-700" />
                        {/* Power button (right side) */}
                        <div className="absolute -right-[3px] top-[220px] w-[3px] h-[100px] rounded-r bg-zinc-700" />

                        {/* Inner black bezel */}
                        <div className="relative w-full h-full rounded-[57px] bg-black p-[10px]">
                            {/* Screen surface — App renders in-place. Container is constrained to
                                phone dimensions so App's max-w-lg + mobile layout paint naturally
                                inside it. `overflow-hidden` prevents FAB / fixed toasts leaking
                                past the bezel. */}
                            <div
                                className="relative w-full h-full rounded-[48px] overflow-hidden bg-black"
                                style={{ boxShadow: "inset 0 0 4px rgba(0,0,0,0.9)" }}
                            >
                                {/* Dynamic Island */}
                                <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[120px] h-[35px] bg-black rounded-full z-[999] pointer-events-none" />

                                {/* App renders in-place. `transform: translateZ(0)` on this
                                    container creates a new containing block, which is critical:
                                    it forces all `position: fixed` children (screensaver,
                                    toasts, dialogs) to anchor to THIS box instead of the
                                    viewport — so they stay inside the phone bezel. */}
                                <div
                                    className="w-full h-full overflow-auto"
                                    style={{ transform: "translateZ(0)" }}
                                >
                                    <App />
                                </div>

                                {/* Home indicator bar (bottom) */}
                                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 w-[134px] h-[5px] bg-white/60 rounded-full z-[999] pointer-events-none" />
                            </div>
                        </div>
                    </div>

                    {/* Ground shadow beneath phone */}
                    <div
                        className="absolute -bottom-6 left-1/2 -translate-x-1/2 w-[70%] h-[30px] rounded-[50%] blur-2xl"
                        style={{ background: "rgba(0,0,0,0.7)" }}
                        aria-hidden="true"
                    />
                </div>

                <div className="text-[10px] font-mono text-zinc-600 max-w-md text-center px-4 pt-4">
                    live preview of the mobile app · shares your session · reload to pull latest
                </div>
            </div>
        );
    }
    return <App />;
}
